import {sortByKey, uniqueObjects, unravel} from "./util";
import {getEntityConfigs} from "@/entityConfigs";
import {getPropertyDisplayName} from "@/metaCatalog";
import {collectionFilterLabel} from "@/collectionFilter";
import countryCodeLookup from "country-code-lookup";
import {continentForCountryCode} from "@/continents";
import {globalSouthCountryCodes} from "@/globalSouth";
import {listedInLabel} from "@/listedIn";

// Alternate names are alternatives *to* the display name, so the display name
// itself should never appear in the list.
const altNames = (entity, field) =>
    (entity[field] || []).filter(name => name && name !== entity.display_name);

// Works booleans that are FILTER-ONLY: elastic-api derives them at query time
// and the work JSON has no such key, so their table + CSV columns were always
// blank (ZD 24281, oxjob #1209). These derive the same value from the work JSON
// with the elastic-api filter semantics. The CSV server mirrors each one in
// openalex-users-api/formats/csv_manifest.py WORK_DERIVED_BOOLEANS — keep the
// two in lockstep.
const workIds = (w) => w?.ids || {};
const derivedWorkBoolean = {
    has_doi: (w) => !!(workIds(w).doi || w?.doi),
    has_pmid: (w) => !!workIds(w).pmid,
    has_pmcid: (w) => !!workIds(w).pmcid,
    has_orcid: (w) => (w?.authorships || []).some((a) => !!a?.author?.orcid),
    has_abstract: (w) => w?.abstract_inverted_index != null,
    has_references: (w) => (w?.referenced_works || []).length > 0 || (w?.referenced_works_count || 0) > 0,
    has_oa_submitted_version: (w) =>
        (w?.locations || []).some((l) => !!l?.is_oa && l?.version === "submittedVersion"),
    mag_only: (w) => !!workIds(w).mag
        && !["pmid", "pmcid", "doi", "arxiv"].some((k) => workIds(w)[k]),
    is_oa: (w) => !!w?.open_access?.is_oa,
    "primary_location.source.has_issn": (w) => {
        const src = w?.primary_location?.source;
        return !!((src?.issn || []).length || src?.issn_l);
    },
    "authorships.institutions.is_global_south": (w) => (w?.authorships || []).some((a) =>
        (a?.institutions || []).some((i) =>
            globalSouthCountryCodes.has((i?.country_code || "").toUpperCase()))),
};

const facetCategories = {
    works: [
        "search",
        "aboutness",
        "author",
        "source",
        "funder",
        "institution",
        "geo",
        "open access",
        "citation",
        "dates",
        "ids",
        "other",
    ],
    authors: [
        "search",
        "institution",
        "geo",
        "citation",
        "ids",
        "other",
    ],
    sources: [
        "search",
        "aboutness",
        "open access",
        "geo",
        "citation",
        "dates",
        "ids",
        "other",
    ],
    publishers: [
        "geo",
        "citation",
        "ids",
        "other",
    ],
    funders: [
        "search",
        "geo",
        "citation",
        "ids",
        "other",
    ],
    institutions: [
        "search",
        "geo",
        "citation",
        "ids",
        "other",
    ],
    concepts: [
        "citation",
        "ids",
        "other",
    ],
    locations: [
        "source",
        "open access",
        "ids",
        "other",
    ],
    awards: [
        "aboutness",
        "funder",
        "investigator",
        "institution",
        "geo",
        "dates",
        "ids",
        "other",
    ],
    topics: [
        "citation",
        "ids",
        "other",
    ],
    subfields: [
        "citation",
        "other",
    ],
    fields: [
        "citation",
        "other",
    ],
    domains: [
        "citation",
        "other",
    ],
    types: [
        "citation",
        "other",
    ],
    continents: [
        "citation",
        "other",
    ],
    countries: [
        "citation",
        "other",
    ],
    languages: [
        "citation",
        "other",
    ],
    sdgs: [
        "citation",
        "other",
    ],
    "source-types": [
        "citation",
        "other",
    ],
    "institution-types": [
        "citation",
        "other",
    ],
    licenses: [
        "citation",
        "other",
    ],
    "oa-statuses": [
        "citation",
        "other",
    ],
    indexes: [
        "citation",
        "other",
    ],
    "source-lists": [
        "citation",
        "other",
    ],
    "study-designs": [
        "citation",
        "other",
    ],
    keywords: [
        "citation",
        "other",
    ],
}

const facetCategoriesIcons = {
    search: "mdi-magnify",
    author: "mdi-account-outline",
    institution: "mdi-town-hall",
    geo: "mdi-map-marker-outline",
    funder: "mdi-cash-multiple",
    source: "mdi-book-multiple-outline",
    "open access": "mdi-lock-open-outline",
    ids: "mdi-tag-outline",
    citation: "mdi-format-quote-close",
    aboutness: "mdi-lightbulb-outline",
    investigator: "mdi-account-outline",
    dates: "mdi-calendar-range",
    other: "mdi-dots-horizontal",
}


const facetConfigs = function (entityType) {
    const ret = [
        // ============================================================
        // WORKS
        // ============================================================
        {
            key: "ids.openalex",
            entityToFilter: "works",
            entityToSelect: "works",
            isId: true,
            type: "selectEntity",
            category: "ids",
            actions: ["column"],
            icon: "mdi-file-document-outline",
            extractFn: (entity) => entity.id,
            // As a COLUMN this is the work's own OpenAlex ID — label it "Work ID"
            // (the filter keeps "Work") and render/export the short form (W…),
            // not the canonical URL. `bare_openalex_id` strips the URL prefix
            // server-side so the CSV matches the table. See OWN_ID_COLUMN below.
            column: {
                label: "Work ID",
                render: { kind: "stringList", bareId: true },
                export: { recipe: "bare_openalex_id" },
            },
        },
        {
            key: "doi",
            entityToFilter: "works",
            entityToSelect: "works",
            isId: true,
            type: "selectEntity",
            category: "ids",
            actions: ["filter"],
            icon: "mdi-file-document-outline",
            extractFn: (entity) => entity.doi,
        },
        {
            key: "concepts.id",
            entityToFilter: "works",
            entityToSelect: "concepts",
            type: "selectEntity",
            isManyOptions: true,
            category: "aboutness",
            actions: [],
            actionsPopular: [],
            icon: "mdi-tag-outline",
            extractFn: (entity) => entity.concepts,
        },
        {
            key: "primary_topic.id",
            entityToFilter: "works",
            entityToSelect: "topics",
            type: "selectEntity",
            isManyOptions: true,
            category: "aboutness",
            actions: ["filter", "group_by", "edit"],
            actionsPopular: ["group_by"],
            icon: "mdi-tag-outline",
            extractFn: (entity) => entity.primary_topic,
        },
        {
            key: "keywords.id",
            entityToFilter: "works",
            entityToSelect: "keywords",
            type: "selectEntity",
            isManyOptions: true,
            category: "aboutness",
            actions: ["filter", "group_by", "edit"],
            actionsPopular: [],
            icon: "mdi-tag-outline",
            extractFn: (entity) => entity.keywords,
        },
        {
            key: "primary_topic.subfield.id",
            entityToFilter: "works",
            entityToSelect: "subfields",
            type: "selectEntity",
            isManyOptions: true,
            category: "aboutness",
            actions: ["filter", "group_by",],
            actionsPopular: [],
            icon: "mdi-tag-outline",
            extractFn: (entity) => entity.primary_topic?.subfield,
        },
        {
            key: "primary_topic.field.id",
            entityToFilter: "works",
            entityToSelect: "fields",
            type: "selectEntity",
            isManyOptions: true,
            category: "aboutness",
            actions: ["filter", "group_by",],
            actionsPopular: [],
            icon: "mdi-tag-outline",
            extractFn: (entity) => entity.primary_topic?.field,
        },
        {
            key: "primary_topic.domain.id",
            entityToFilter: "works",
            entityToSelect: "domains",
            type: "selectEntity",
            isManyOptions: true,
            category: "aboutness",
            actions: ["filter", "group_by",],
            actionsPopular: [],
            icon: "mdi-tag-outline",
            extractFn: (entity) => entity.primary_topic?.domain,
        },
        {
            key: "awards.id",
            entityToFilter: "works",
            entityToSelect: "awards",
            type: "selectEntity",
            isManyOptions: true,
            category: "funder",
            actions: ["filter", "edit"],
            actionsPopular: [],
            icon: "mdi-cash-multiple",
            extractFn: (entity) => {
                if (!entity.awards) return [];
                return entity.awards.map(award => {
                    if (!award) return null;
                    // Extract short ID (e.g., "G5453342221" from "https://openalex.org/G5453342221")
                    const shortId = award.id?.split('/').pop() || award.id;
                    return {
                        id: award.id,
                        display_name: award.title || award.display_name || shortId
                    };
                }).filter(a => a !== null);
            },
        },
        {
            key: "funders.id",
            entityToFilter: "works",
            entityToSelect: "funders",
            type: "selectEntity",
            isManyOptions: true,
            category: "funder",
            actions: ["filter", "group_by"],
            actionsPopular: ["group_by"],
            icon: "mdi-cash-multiple",
            semanticSearchAllowed: true,
            // The client extractFn dedupes funders; the server's mechanical
            // flatten doesn't. Apply the `unique` recipe on both name + :ids
            // variants to match. Path auto-derives ("funders.display_name").
            column: { export: { recipe: "unique" } },
            extractFn: (entity) => {
                const funders = entity.funders || [];
                return uniqueObjects(funders.filter(funder => funder?.id));
            },
        },
        {
            key: "authorships.institutions.lineage",
            entityToFilter: "works",
            entityToSelect: "institutions",
            type: "selectEntity",
            isManyOptions: true,
            category: "institution",
            actions: ["filter", "group_by",],
            actionsPopular: ["filter", "group_by",],
            icon: "mdi-town-hall",
            semanticSearchAllowed: true,
            // The key is `lineage` (a filter affordance — accepts an
            // institution's ancestor IDs), but the COLUMN renders the
            // deduped per-author institutions. Server has no `lineage`
            // flat-path matching this concept; map to display_name + dedupe.
            // The :ids variant auto-derives to `authorships.institutions.id`.
            column: {
                export: {
                    path: "authorships.institutions.display_name",
                    recipe: "unique",
                },
            },
            extractFn: (entity) => {
                const nested = entity.authorships.map(authorship => {
                    return authorship.institutions
                })
                // Filter out institutions with null id (fully null objects from API)
                const filtered = nested.flat().filter(inst => inst && inst.id)
                return uniqueObjects(filtered)
            },
        },
        {
            // The server's "exact institution" filter: works with an author at this
            // institution itself, NOT its child institutions (that's `.lineage`
            // above, the curated "institution" facet). Keep them distinct; the
            // counts differ. This config exists so `institutions.id:` /
            // `authorships.institutions.id:` URLs render a chip and stay in Basic
            // mode; hidden from the pickers so they keep offering one
            // "institution" (#655).
            key: "authorships.institutions.id",
            entityToFilter: "works",
            entityToSelect: "institutions",
            type: "selectEntity",
            isManyOptions: true,
            category: "institution",
            actions: ["filter",],
            actionsPopular: [],
            hideFromPicker: true,
            icon: "mdi-town-hall",
        },
        {
            key: "authorships.institutions.ror",
            entityToFilter: "works",
            entityToSelect: "institutions",
            isId: true,
            type: "selectEntity",
            category: "ids",
            actions: ["filter", "column"],
            icon: "mdi-town-hall",
            // extractFn returns an array of full ROR URLs -> stringList auto-linkifies each.
            column: { render: { kind: "stringList" } },
            extractFn: (entity) => {
                const nested = entity.authorships.map(authorship => {
                    // Filter out institutions with null id before accessing ror property
                    return authorship.institutions.filter(insti => insti && insti.id).map(insti => insti.ror)
                })
                return nested.flat()
            },
        },
        {
            key: "authorships.author.id",
            entityToFilter: "works",
            entityToSelect: "authors",
            type: "selectEntity",
            isManyOptions: true,
            category: "author",
            actions: ["filter", "group_by", "edit", "column"],
            actionsPopular: ["column"],
            icon: "mdi-account-outline",
            semanticSearchAllowed: true,
            // entityList of author objects. itemLabelField/itemLinkField name the
            // snake_case API fields on each extracted item (display_name / id) —
            // NOT the camelCase `displayName` property label above.
            column: { render: { kind: "entityList", itemLabelField: "display_name", itemLinkField: "id" } },
            extractFn: (entity) => {
                return entity.authorships.map(authorship => {
                    // If we have a full author object, return it with raw_author_name attached
                    if (authorship.author) {
                        return {
                            ...authorship.author,
                            // Include raw_author_name for display on entity pages
                            raw_author_name: authorship.raw_author_name
                        };
                    }
                    // If we only have raw_author_name, create a pseudo-object
                    // This won't have an ID (so no link), but will have a display_name
                    if (authorship.raw_author_name) {
                        return {
                            display_name: authorship.raw_author_name,
                            raw_author_name: authorship.raw_author_name,
                            id: null // No ID means EntityDatumRow won't create a link
                        };
                    }
                    // No author info at all
                    return null;
                }).filter(author => author !== null); // Remove nulls
            },
        },
        {
            key: "authorships.author.orcid",
            entityToFilter: "works",
            entityToSelect: "authors",
            isId: true,
            type: "selectEntity",
            category: "ids",
            actions: ["filter", "edit"],
            icon: "mdi-account-outline",
            extractFn: (entity) => {
                return entity.authorships.map(authorship => {
                    return authorship.author.orcid
                })
            },
        },
        // works: search
        {
            // Searches title + abstract + full-text body together (NOT
            // author/affiliation). Labeled "full text" per the Europe PMC
            // convention, where "full text" means the complete article text.
            // As of oxjob #374, fulltext.search is the canonical broad scope
            // (it used to be body-only); default.search is the deprecated alias
            // and is intentionally not surfaced as a chip here. See zd#8310.
            key: "fulltext.search",
            entityToFilter: "works",
            type: "search",
            actions: ["filter",],
            actionsPopular: [],
            category: "search",
            icon: "mdi-magnify",
        },
        {
            key: "title_and_abstract.search",
            entityToFilter: "works",
            // "title-abstract" keeps the two words cohering as one scope and
            // avoids the false-conjunction read of "title and abstract" (oxjob
            // #374 chose the PubMed [tiab] slash; hyphen since 2026-10-03, #1521/#1512,
            // because slashes belong to ids; "title/abstract" is still accepted).
            type: "search",
            actions: ["filter",],
            actionsPopular: ["filter"],
            category: "search",
            icon: "mdi-magnify",
        },
        {
            // Title + abstract text OR the keywords a phrase in the query names
            // (oxjob #1521); the search box's default scope.
            key: "title_abstract_keywords.search",
            entityToFilter: "works",
            type: "search",
            actions: ["filter",],
            actionsPopular: ["filter"],
            category: "search",
            icon: "mdi-magnify",
        },
        {
            key: "display_name.search",
            entityToFilter: "works",
            actions: ["filter",],
            actionsPopular: [],
            type: "search",
            category: "search",
            icon: "mdi-magnify",
        },
        {
            // Alias for display_name.search to support URLs like
            // /works?filter=title.search:ai . The server folded this into
            // display_name.search's alternate_keys (#446) and both render the same
            // "title" label, so it's HIDDEN from the field pickers (it would show as
            // a duplicate "title"); the facetConfig stays for URL/createFilter/export
            // parsing of legacy title.search: filters. (oxjob #505)
            key: "title.search",
            entityToFilter: "works",
            displayName: "title",
            actions: ["filter",],
            actionsPopular: [],
            type: "search",
            category: "search",
            hideFromPicker: true,
            icon: "mdi-magnify",
        },
        {
            // Raw affiliation was never stemmed, so its search has only the exact mode;
            // old `raw_affiliation_strings.search` URLs fold here through the catalog's
            // alternate_keys (oxjob #400).
            key: "raw_affiliation_strings.search.exact",
            entityToFilter: "works",
            type: "search",
            actions: ["filter",],
            actionsPopular: ["",],
            category: "search",
            icon: "mdi-magnify",
        },
        {
            key: "raw_affiliation_strings",
            entityToFilter: "works",
            type: "search",
            actions: ["filter",],
            actionsPopular: [],
            category: "search",
            icon: "mdi-map-marker-outline",
            verb: "is exactly",
        },
        {
            key: "doi_starts_with",
            entityToFilter: "works",
            type: "search",
            actions: ["filter",],
            actionsPopular: [],
            category: "ids",
            icon: "mdi-magnify",
            verb: "starts with",
        },
        {
            key: "display_name",
            isIdentityColumn: true,
            entityToFilter: "works",
            type: "search",
            actions: ["sort", "column", "edit"],
            actionsPopular: ["sort", "column",],
            category: "other",
            icon: "mdi-file-document-outline",
            // The mandatory identity column. ResultsTable renders it as a link
            // to the row's own entity (passes the whole row object), so the
            // entityLink kind reads display_name + id off the work itself.
            column: { render: { kind: "entityLink" } },
            extractFn: (entity) => entity.display_name,
        },
        {
            key: "has_abstract",
            entityToFilter: "works",
            extractFn: derivedWorkBoolean["has_abstract"],
            type: "boolean",
            booleanValues: ["No abstract", "Has abstract"],
            actions: ["filter"],
            category: "other",
            icon: "mdi-file-document-outline",
            semanticSearchAllowed: true,
        },

        // works: authors
        {
            key: "authors_count",
            entityToFilter: "works",
            type: "range",
            category: "author",
            actions: ["filter", "sort", "column",],
            icon: "mdi-account-outline",
            extractFn: (entity) => entity.authors_count,
        },
        {
            key: "corresponding_author_ids",
            entityToFilter: "works",
            entityToSelect: "authors",
            type: "selectEntity",
            isManyOptions: true,
            category: "author",
            actions: ["filter", "group_by", "edit", "column"],
            icon: "mdi-email-outline",
            // Column/export support (ZD #21941). The work JSON carries a
            // top-level `corresponding_author_ids` list of author OpenAlex URLs.
            // Like the ROR column, the extractFn returns those URLs as a
            // stringList (auto-linkified in the table); deriveExportPath sends
            // the same flat path, so the CSV ships it verbatim. Names live on
            // authorships, not this path — so this restores exactly the column
            // the old dump-all export shipped (which the new column picker
            // dropped because the config had no extractFn / column.render).
            column: { render: { kind: "stringList" } },
            extractFn: (entity) => entity.corresponding_author_ids || [],
        },
        // works: open access
        {
            key: "open_access.is_oa",
            entityToFilter: "works",
            type: "boolean",
            booleanValues: ["NOT Open Access", "Open Access"],
            actions: ["filter", "column", "group_by",],
            actionsPopular: ["filter", "column", "group_by",],
            category: "open access",
            icon: "mdi-lock-open-outline",
            semanticSearchAllowed: true,
            // CellValue reads booleanValues above for the rendered labels.
            column: { render: { kind: "boolean" } },
            extractFn: (entity) => entity.open_access?.is_oa,
        },
        {
            key: "has_content.pdf",
            entityToFilter: "works",
            type: "boolean",
            booleanValues: ["not linked to a PDF", "linked to a PDF"],
            actions: ["filter", "column", "group_by",],
            actionsPopular: [],
            category: "open access",
            icon: "mdi-file-pdf-box",
        },
        {
            key: "best_oa_location.license",
            entityToFilter: "works",
            entityToSelect: "licenses",
            type: "selectEntity",
            // actions: [],
            actions: ["filter", "column", "group_by"],
            category: "open access",
            icon: "mdi-lock-open-outline",
            displayNullAs: "All rights reserved",
        },
        {   key: "locations.license", // Added as stub to prevent JS errors
            entityToFilter: "works",
            entityToSelect: "licenses",
            displayName: "any location license",
            type: "selectEntity",
            actions: ["filter", "edit"],
            category: "open access",
            icon: "mdi-lock-open-outline",
            displayNullAs: "All rights reserved",
        },
        {
            // works but with workarounds because entity endpoints don't exist
            key: "open_access.oa_status",
            entityToSelect: "oa-statuses",
            entityToFilter: "works",
            displayName: "Open Access status",
            type: "selectEntity",
            actions: ["filter", "column", "group_by","edit"],
            category: "open access",
            icon: "mdi-lock-open-outline",
            extractFn: (entity) => entity.open_access.oa_status,
            column: { export: { path: "open_access.oa_status" } },
        },
        {
            key: "best_oa_location.is_accepted",
            entityToFilter: "works",
            type: "boolean",
            booleanValues: ["NOT Open Access", "Open Access"],
            actions: ["filter", "column", "group_by",],
            category: "open access",
            icon: "mdi-lock-open-outline",
        },
        {
            key: "best_oa_location.is_published",
            entityToFilter: "works",
            type: "boolean",
            booleanValues: ["NOT Open Access", "Open Access"],
            actions: ["filter", "column", "group_by",],
            category: "open access",
            icon: "mdi-lock-open-outline",
        },
        // works: APC
        {
            key: "apc_paid.value_usd",
            entityToFilter: "works",
            type: "range",
            category: "other",
            actions: ["filter","edit","column"],
            icon: "mdi-cash",
            column: { render: { kind: "currency", currency: "USD" } },
            extractFn: (entity) => entity.apc_paid?.value_usd,
        },

        // works: primary source
        {
            key: "primary_location.source.id",
            entityToFilter: "works",
            entityToSelect: "sources",
            type: "selectEntity",
            isManyOptions: true,
            category: "source",
            actions: ["filter", "group_by",],
            icon: "mdi-book-open-outline",
            semanticSearchAllowed: true,
            extractFn: (entity) => entity.primary_location.source,
        },

        // works: the work this work is part of, e.g. a chapter's book (oxjob #1554).
        // A work id; the filter lists a book's chapters. displayName must equal the
        // registry display_name (label-consistency gate).
        {
            key: "volume.id",
            entityToFilter: "works",
            entityToSelect: "works",
            displayName: "volume",
            type: "selectEntity",
            isManyOptions: true,
            category: "source",
            actions: ["filter", "group_by",],
            icon: "mdi-book-open-page-variant-outline",
            extractFn: (entity) => entity.volume,
        },

        // works: source across all locations (primary + alternate)
        {
            key: "locations.source.id",
            entityToFilter: "works",
            entityToSelect: "sources",
            type: "selectEntity",
            isManyOptions: true,
            category: "source",
            actions: ["filter"],
            icon: "mdi-book-open-outline",
            // The column name says "any location" — extract the source from
            // every location, not just primary. Filter out locations whose
            // .source is null (e.g. webpages).
            extractFn: (entity) => (entity.locations ?? [])
                .map((loc) => loc.source)
                .filter((s) => s),
        },
        {
            key: "primary_location.source.issn",
            entityToFilter: "works",
            entityToSelect: "sources",
            isId: true,
            type: "selectEntity",
            category: "ids",
            actions: ["filter"],
            icon: "mdi-book-open-outline",
            // Column-eligible (CSV export + table view): the dehydrated source
            // on works carries the issn list; server flat-path is
            // `primary_location.source.issn` (|-joined).
            extractFn: (entity) => entity.primary_location?.source?.issn,
            noIdsSibling: true,
        },
        {
            key: "primary_location.source.type",
            entityToFilter: "works",
            entityToSelect: "sources",
            type: "selectEntity",
            category: "source",
            actions: ["filter", "column", "group_by",],
            icon: "mdi-book-open-outline",
        },
        // oxjob #1526: query-time join filters (looked up in the source, publisher, funder or
        // institution index). Filter only; `noGroupBy` makes pickers list values locally.
        {
            key: "primary_location.source.country_code",
            entityToFilter: "works",
            entityToSelect: "countries",
            displayName: "source country",
            type: "selectEntity",
            isManyOptions: true,
            isCountry: true,
            noGroupBy: true,
            category: "source",
            actions: ["filter"],
            actionsPopular: [],
            icon: "mdi-earth",
        },
        {
            key: "primary_location.source.is_global_south",
            entityToFilter: "works",
            displayName: "source global south",
            type: "boolean",
            booleanValues: ["Source not in Global South", "Source in Global South"],
            noGroupBy: true,
            category: "source",
            actions: ["filter"],
            icon: "mdi-book-open-outline",
        },
        {
            key: "primary_location.source.is_ojs",
            entityToFilter: "works",
            displayName: "source OJS",
            type: "boolean",
            booleanValues: ["Source not on OJS", "Source on OJS"],
            noGroupBy: true,
            category: "source",
            actions: ["filter"],
            icon: "mdi-book-open-outline",
        },
        {
            key: "primary_location.source.is_high_oa_rate",
            entityToFilter: "works",
            displayName: "source high OA rate",
            type: "boolean",
            booleanValues: ["Source without high OA rate", "Source with high OA rate"],
            noGroupBy: true,
            category: "source",
            actions: ["filter"],
            icon: "mdi-book-open-outline",
        },
        {
            key: "primary_location.source.is_preprint_repository",
            entityToFilter: "works",
            displayName: "source preprint repository",
            type: "boolean",
            booleanValues: ["Not a preprint repository", "Preprint repository"],
            noGroupBy: true,
            category: "source",
            actions: ["filter"],
            icon: "mdi-book-open-outline",
        },
        {
            key: "primary_location.source.summary_stats.h_index",
            entityToFilter: "works",
            displayName: "source h-index",
            type: "range",
            category: "source",
            actions: ["filter"],
            actionsPopular: [],
            icon: "mdi-chart-line",
        },
        {
            key: "primary_location.source.summary_stats.2yr_mean_citedness",
            entityToFilter: "works",
            displayName: "source 2-year mean citedness",
            type: "range",
            category: "source",
            actions: ["filter"],
            actionsPopular: [],
            icon: "mdi-chart-line",
        },
        {
            key: "primary_location.source.host_organization.country_code",
            entityToFilter: "works",
            entityToSelect: "countries",
            displayName: "publisher country",
            type: "selectEntity",
            isManyOptions: true,
            isCountry: true,
            noGroupBy: true,
            category: "source",
            actions: ["filter"],
            actionsPopular: [],
            icon: "mdi-earth",
        },
        {
            key: "funders.country_code",
            entityToFilter: "works",
            entityToSelect: "countries",
            displayName: "funder country",
            type: "selectEntity",
            isManyOptions: true,
            isCountry: true,
            noGroupBy: true,
            category: "funder",
            actions: ["filter"],
            actionsPopular: [],
            icon: "mdi-earth",
        },
        {
            key: "authorships.institutions.geo.region",
            entityToFilter: "works",
            displayName: "institution region",
            type: "search",
            quoteValue: true,
            category: "institution",
            actions: ["filter"],
            actionsPopular: [],
            icon: "mdi-map-marker-outline",
        },
        {
            key: "authorships.institutions.geo.city",
            entityToFilter: "works",
            displayName: "institution city",
            type: "search",
            quoteValue: true,
            category: "institution",
            actions: ["filter"],
            actionsPopular: [],
            icon: "mdi-map-marker-outline",
        },
        {
            key: "primary_location.source.is_in_doaj",
            entityToFilter: "works",
            type: "boolean",
            booleanValues: ["Not in DOAJ", "In DOAJ"],
            category: "source",
            actions: ["filter", "group_by",],
            icon: "mdi-book-open-outline",
        },
        {
            key: "primary_location.source.is_core",
            entityToFilter: "works",
            type: "boolean",
            booleanValues: ["Not CWTS Core source", "CWTS Core source"],
            category: "source",
            actions: ["filter", "group_by",],
            icon: "mdi-book-open-outline",
        },
        {
            // oxjob #1205: see sources `listed_in`.
            key: "primary_location.source.listed_in",
            entityToFilter: "works",
            entityToSelect: "source-lists",
            displayName: "source listed in",
            type: "selectEntity",
            category: "source",
            actions: ["filter", "group_by",],
            icon: "mdi-format-list-bulleted",
        },
        {
            key: "primary_location.source.is_oa",
            entityToFilter: "works",
            type: "boolean",
            booleanValues: ["Not Open Access", "Open Access"],
            category: "source",
            actions: ["filter", "column", "group_by",],
            icon: "mdi-book-open-outline",
        },
        {
            // Canonical key (#455); the old alias `primary_location.source.publisher_lineage`
            // still resolves via the fold in facetConfigUtils.getFacetConfig.
            key: "primary_location.source.host_organization_lineage",
            entityToFilter: "works",
            entityToSelect: "publishers",
            type: "selectEntity",
            isManyOptions: true,
            category: "source",
            actions: ["filter", "group_by",],
            icon: "mdi-domain",
        },

        // works: institutions
        {
            key: "authorships.countries",
            entityToFilter: "works",
            entityToSelect: "countries",
            displayName: "Country",
            type: "selectEntity",
            isManyOptions: true,
            isCountry: true,
            actions: ["filter", "column", "group_by","edit"],
            actionsPopular: [],
            category: "geo",
            icon: "mdi-earth",
            // Column/export support. Without an extractFn this selectEntity
            // derives an entityList render kind but no usable extractFn, so
            // isColumnEligible() drops it — which is why "Country" was silently
            // absent from the column picker + CSV export (zd#8973). Render the
            // deduped country names in the table (countryCodeLookup, like
            // last_known_institutions.country_code); the CSV ships the raw ISO
            // codes via the authorships.countries flat path — the accepted
            // CODE_VS_NAME divergence (see parity.sweep.test.js).
            column: { render: { kind: "stringList" }, export: { path: "authorships.countries" } },
            extractFn: (entity) => {
                if (!Array.isArray(entity.authorships)) return [];
                const seen = new Set();
                const names = [];
                for (const authorship of entity.authorships) {
                    for (const code of (authorship.countries || [])) {
                        if (!code || seen.has(code)) continue;
                        seen.add(code);
                        const result = countryCodeLookup.byIso(String(code).toUpperCase());
                        names.push(result?.country || code);
                    }
                }
                return names;
            },
        },
        {
            key: "countries_distinct_count",
            entityToFilter: "works",
            type: "range",
            actions: ["filter", "sort", "column",],
            category: "geo",
            icon: "mdi-earth",
        },
        {
            key: "institutions_distinct_count",
            entityToFilter: "works",
            type: "range",
            actions: ["filter", "sort", "column",],
            category: "institution",
            icon: "mdi-earth",
        },
        {
            key: "authorships.institutions.continent",
            entityToFilter: "works",
            entityToSelect: "continents",
            displayName: "Continent",
            type: "selectEntity",
            actions: ["filter", "column", "group_by",],
            actionsPopular: [],
            category: "geo",
            icon: "mdi-earth",
            // Column/export support. Like authorships.countries (zd#8973), this
            // had "column" in actions but no extractFn, so isColumnEligible()
            // dropped it from the picker. Unlike countries, the work JSON has NO
            // continent field — institutions carry only country_code — so both
            // the table and the CSV DERIVE continent from country codes. The
            // CSV exports the existing country_code flat path through the
            // server-side `country_to_continent` recipe, which mirrors the same
            // @/continents map (see openalex-users-api/formats/csv_manifest.py).
            column: {
                render: { kind: "stringList" },
                export: { path: "authorships.institutions.country_code", recipe: "country_to_continent" },
            },
            extractFn: (entity) => {
                if (!Array.isArray(entity.authorships)) return [];
                const seen = new Set();
                const continents = [];
                for (const authorship of entity.authorships) {
                    for (const institution of (authorship.institutions || [])) {
                        const continent = continentForCountryCode(institution.country_code);
                        if (!continent || seen.has(continent)) continue;
                        seen.add(continent);
                        continents.push(continent);
                    }
                }
                return continents;
            },
        },
        {
            // Canonical key (#455); the old alias `institutions.is_global_south`
            // still resolves via the fold in facetConfigUtils.getFacetConfig.
            key: "authorships.institutions.is_global_south",
            entityToFilter: "works",
            extractFn: derivedWorkBoolean["authorships.institutions.is_global_south"],
            type: "boolean",
            actions: ["filter", "column", "group_by",],
            category: "geo",
            booleanValues: ["Global North", "Global South"],
            icon: "mdi-earth",
            // icon: "mdi-town-hall",
        },
        {
            key: "authorships.institutions.type",
            entityToFilter: "works",
            entityToSelect: "institution-types",
            category: "institution",
            type: "selectEntity",
            actions: ["filter", "group_by",],
            icon: "mdi-town-hall",
        },
        {
            key: "corresponding_institution_ids",
            entityToFilter: "works",
            entityToSelect: "institutions",
            category: "institution",
            type: "selectEntity",
            isManyOptions: true,
            actions: ["filter", "group_by", "column"],
            icon: "mdi-email-outline",
            // Column/export support (ZD #21941) — see corresponding_author_ids
            // above. Top-level `corresponding_institution_ids` list of
            // institution OpenAlex URLs; useful for APC analysis (which org
            // pays). stringList render + verbatim flat-path export.
            column: { render: { kind: "stringList" } },
            extractFn: (entity) => entity.corresponding_institution_ids || [],
        },

        // works: repository
        {
            key: "open_access.any_repository_has_fulltext",
            entityToFilter: "works",
            type: "boolean",
            booleanValues: ["Not in any repository", "In a repository"],
            category: "source",
            actions: ["filter", "column", "group_by",],
            icon: "mdi-tag-outline",
        },

        // works: intrinsic
        {
            key: "type",
            entityToFilter: "works",
            entityToSelect: "types",
            type: "selectEntity",
            category: "other",
            actions: ["filter", "column", "group_by","edit"],
            actionsPopular: ["filter", "column", "group_by",],
            icon: "mdi-shape-outline",
            extractFn: (entity) => entity.type,
            semanticSearchAllowed: true,
            column: { export: { path: "type" } },
        },
        {
            key: "abstract",
            entityToFilter: "works",
            displayName: "Abstract",
            type: "search",
            category: "other",
            actions: ["edit", "column"],
            icon: "mdi-text",
            // Opt this search-type property in as a text column (the explicit
            // block overrides the type-based derivation, which skips `search`).
            // extractFn reconstructs the abstract from the inverted index.
            column: { render: { kind: "text" } },
            extractFn: (entity) => {
                if (!entity?.open_access?.is_oa) return
                return unravel(entity.abstract_inverted_index)
            },
        },
        {
            key: "publication_year",
            entityToFilter: "works",
            isDate: true,
            type: "range",
            category: "dates",
            actions: ["filter", "sort", "column", "group_by",],
            actionsPopular: ["filter", "sort", "column", "group_by",],
            icon: "mdi-calendar-range",
            column: { render: { kind: "year" } },
            extractFn: (entity) => entity.publication_year,
            semanticSearchAllowed: true,
        },
        {
            key: "from_created_date",
            entityToFilter: "works",
            displayName: "Created since date",
            isDate: true,
            type: "search",
            requiresApiKey: true,
            category: "dates",
            actions: ["filter", "sort", "column"],
            actionsPopular: ["sort", "column"],
            icon: "mdi-calendar-range",
            extractFn: (entity) => entity.created_date,
        },
        {
            key: "from_updated_date",
            entityToFilter: "works",
            displayName: "Updated since date",
            isDate: true,
            type: "search",
            requiresApiKey: true,
            category: "dates",
            actions: ["filter", "sort", "column"],
            actionsPopular: ["sort", "column"],
            icon: "mdi-calendar-range",
            extractFn: (entity) => entity.updated_date,
        },


        {
            key: "apc_sum",
            entityToFilter: "works",
            displayName: "APC sum",
            type: "sum",
            category: "other",
            actions: ["group_by",],
            actionsPopular: [],
            icon: "mdi-cash",
        },
        {
            key: "cited_by_count_sum",
            entityToFilter: "works",
            displayName: "Citations sum",
            type: "sum",
            category: "other",
            actions: ["group_by",],
            actionsPopular: [],
            icon: "mdi-format-quote-close",
        },
        {
            key: "publication_date",
            entityToFilter: "works",
            isDate: true,
            type: "range",
            actions: ["sort",],
            category: "dates",
            icon: "mdi-calendar-range",
        },
        {
            key: "has_doi",
            entityToFilter: "works",
            extractFn: derivedWorkBoolean["has_doi"],
            type: "boolean",
            booleanValues: ["No DOI", "Has a DOI"],
            category: "ids",
            actions: ["filter", "group_by",],
            icon: "mdi-tag-outline",
        },
        {
            key: "indexed_in",
            entityToSelect: "indexes",
            entityToFilter: "works",
            type: "selectEntity",
            category: "ids",
            actions: ["filter", "group_by",],
            icon: "mdi-tag-outline",
        },
        {
            key: "mag_only",
            entityToFilter: "works",
            extractFn: derivedWorkBoolean["mag_only"],
            type: "boolean",
            booleanValues: ["indexed beyond MAG", "indexed by MAG only"],
            category: "ids",
            actions: ["filter", "group_by",],
            icon: "mdi-tag-outline",
        },
        {
            key: "is_xpac",
            entityToFilter: "works",
            displayName: "extended index",
            type: "boolean",
            booleanValues: ["in core index", "in extended index"],
            category: "other",
            actions: ["filter", "group_by",],
            actionsPopular: [],
            icon: "mdi-new-box",
        },
        {
            key: "has_orcid",
            entityToFilter: "works",
            extractFn: derivedWorkBoolean["has_orcid"],
            type: "boolean",
            booleanValues: ["No ORCID", "At least one ORCID",],
            category: "ids",
            actions: ["filter", "group_by",],
            icon: "mdi-tag-outline",
        },
        {
            key: "has_pmid",
            entityToFilter: "works",
            extractFn: derivedWorkBoolean["has_pmid"],
            type: "boolean",
            category: "ids",
            actions: ["filter", "group_by",],
            icon: "mdi-tag-outline",
            booleanValues: ["No PubMed ID", "Has PubMed ID"],
        },
        {
            key: "is_retracted",
            entityToFilter: "works",
            type: "boolean",
            booleanValues: ["Isn't retracted", "Is retracted"],
            category: "other",
            actions: ["filter", "column", "group_by","edit"],
            icon: "mdi-close-octagon",
            semanticSearchAllowed: true,
        },
        {
            key: "language",
            entityToSelect: "languages",
            entityToFilter: "works",
            type: "selectEntity",
            displayNullAs: "Unknown",
            category: "geo",
            actions: ["filter", "column", "group_by","edit"],
            actionsPopular: ["column"],
            icon: "mdi-translate",
            extractFn: (entity) => entity.language,
            column: { export: { path: "language" } },
        },
        {
            key: "sustainable_development_goals.id",
            entityToSelect: "sdgs",
            entityToFilter: "works",
            type: "selectEntity",
            displayNullAs: "Unknown",
            category: "aboutness",
            actions: ["filter", "group_by","edit"],
            icon: "mdi-sprout-outline",
            extractFn: (entity) => entity.sustainable_development_goals
        },
        {
            // oxjob #1312: how the research was done (RCT, systematic review, ...);
            // `type` says what kind of document it is. Closed study-designs vocabulary;
            // works carry parents too (RCT => clinical trial). displayName must equal
            // the registry display_name (label-consistency gate).
            key: "study_designs.id",
            entityToSelect: "study-designs",
            entityToFilter: "works",
            displayName: "study design",
            type: "selectEntity",
            displayNullAs: "Unknown",
            category: "aboutness",
            actions: ["filter", "group_by"],
            icon: "mdi-flask-outline",
            extractFn: (entity) => entity.study_designs
        },
        {
            key: "cited_by_count",
            entityToFilter: "works",
            type: "range",
            category: "citation",
            actions: ["filter", "sort", "column",],
            actionsPopular: ["sort", "column",],
            icon: "mdi-format-quote-close",
            column: { render: { kind: "number" } },
            extractFn: (entity) => entity.cited_by_count,
        },
        {
            key: "referenced_works_count",
            entityToFilter: "works",
            type: "range",
            category: "citation",
            actions: ["filter", "column",],
            actionsPopular: [],
            icon: "mdi-format-quote-close",
        },
        {
            key: "fwci",
            entityToFilter: "works",
            type: "range",
            category: "citation",
            actions: ["filter", "sort", "column",],
            actionsPopular: ["sort", "column",],
            icon: "mdi-format-quote-close",
            extractFn: (entity) => entity.fwci,
        },
        {
            key: "cited_by",
            entityToFilter: "works",
            entityToSelect: "works",
            type: "selectEntity", // used to be "entity"
            category: "citation",
            actions: ["filter"],
            icon: "mdi-format-quote-close",
            isDisplayedAsCount: true,
            extractFn: (entity) => entity.cited_by_count,
            // Not a table column: a work carries no list of the works citing it,
            // and its count is already the "Cited by count" column (ZD 22167).
            noColumn: true,
        },
        {
            key: "cites",
            entityToFilter: "works",
            entityToSelect: "works",
            displayName: "cites",
            type: "selectEntity", // used to be "entity"
            category: "citation",
            actions: [],
            icon: "mdi-format-quote-close",
            isDisplayedAsCount: true,
            extractFn: (entity) => entity.referenced_works?.length,
            // The table shows the count; the CSV gets the cited works' IDs, as
            // related_to does (ZD 22167: the count only repeated "Reference count").
            column: { export: { path: "referenced_works", recipe: "bare_openalex_id" } },
        },
        {
            key: "related_to",
            entityToFilter: "works",
            entityToSelect: "works",
            type: "selectEntity", // used to be "entity"
            category: "citation",
            actions: ["filter",],
            icon: "mdi-book-open-outline",
            isDisplayedAsCount: true,
            extractFn: (entity) => entity.related_works?.length,
            // No `related_works_count` server field; the table shows the count
            // on screen, the CSV gets the underlying ID list (richer for
            // downstream analysis — matches Jason's "raw values" call on
            // numbers / currency).
            column: { export: { path: "related_works", recipe: "bare_openalex_id" } },
        },

        // ============================================================
        // AUTHORS
        // ============================================================
        {
            key: "ids.openalex",
            entityToFilter: "authors",
            entityToSelect: "authors",
            type: "selectEntity",
            actions: ["column"],
            actionsPopular: [],
            isId: true,
            category: "ids",
            icon: "mdi-account-outline",
            // OWN_ID_COLUMN: the author's own OpenAlex ID, rendered + exported
            // as the short form (A…). See works ids.openalex.
            extractFn: (entity) => entity.id,
            column: {
                label: "Author ID",
                render: { kind: "stringList", bareId: true },
                export: { recipe: "bare_openalex_id" },
            },
        },
        {
            key: "ids.orcid",
            entityToFilter: "authors",
            entityToSelect: "authors",
            displayName: "ORCID",
            isId: true,
            type: "selectEntity",
            category: "ids",
            actions: ["edit"],
            icon: "mdi-account-outline",
            extractFn: (e) => e.ids.orcid
        },
        {
            key: "observed_orcids",
            entityToFilter: "authors",
            displayName: "Observed ORCIDs",
            type: "search",
            isId: true,
            // Column + profile-page row only (oxjob #1340, Jason 2026-09-23: no separate
            // observed_orcids filter — the ORCID filter itself matches any observed ORCID).
            actions: ["column"],
            actionsPopular: [],
            category: "ids",
            icon: "mdi-account-outline",
            extractFn: (e) => e.observed_orcids ?? e.ids?.observed_orcids,
            // Server flat-path is `observed_orcids` (the |-joined list).
            column: { render: { kind: "stringList" }, export: { path: "observed_orcids" } },
            noIdsSibling: true,
        },
        {
            key: "default.search",
            entityToFilter: "authors",
            entityToSelect: "authors",
            displayName: "name",
            type: "search",
            category: "search",
            actions: ["filter"],
            actionsPopular: ["filter"],
            icon: "mdi-magnify",
        },
        {
            key: "display_name",
            isIdentityColumn: true,
            entityToFilter: "authors",
            type: "search",
            actions: ["sort", "column", "edit"],
            actionsPopular: ["sort", "column",],
            category: "other",
            icon: "mdi-account-outline",
            extractFn: (entity) => entity.display_name,
        },
        {
            key: "affiliations.institution.id",
            entityToFilter: "authors",
            displayName: "Past institutions",
            // entity-page row label only (filter UI keeps "Past institutions"): these are
            // institutions OBSERVED in the author's papers' affiliation text, not a curated CV —
            // "observed institutions" rhymes with the "Observed names" row above it and heads off
            // the most common support confusion; "past" is implied (Jason 2026-06-12)
            displayNameOnEntityPage: "observed institution",
            entityToSelect: "institutions",
            type: "selectEntity",
            isManyOptions: true,
            category: "institution",
            actions: ["filter", "group_by"],
            actionsPopular: [],
            icon: "mdi-town-hall",
            extractFn: (entity) => {
                return entity.affiliations.map(affil => {
                    return affil.institution
                })
            },
        },
        {
            key: "affiliations.institution.type",
            entityToFilter: "authors",
            entityToSelect: "institution-types",
            displayName: "Past institutions type",
            type: "selectEntity",
            category: "institution",
            actions: ["filter", "group_by"],
            actionsPopular: [],
            icon: "mdi-shape-outline",
        },
        {
            // "Institution": the last known institution with its child organizations (a
            // university's labs and institutes), as works' "Institution" filter is (oxjob #594).
            key: "last_known_institutions.lineage",
            entityToFilter: "authors",
            entityToSelect: "institutions",
            type: "selectEntity",
            isManyOptions: true,
            category: "institution",
            actions: ["filter", "group_by"],
            actionsPopular: ["filter"],
            icon: "mdi-town-hall",
        },
        {
            // "Exact institution": that institution only. Still the default split, the
            // default column and the entity-page row, which show the institution itself, not
            // its parents; the column and the entity page keep the plain label.
            key: "last_known_institutions.id",
            displayNameOnEntityPage: "institution",
            column: { label: "institution" },
            entityToFilter: "authors",
            entityToSelect: "institutions",
            type: "selectEntity",
            isManyOptions: true,
            category: "institution",
            actions: ["filter", "group_by", "column"],
            actionsPopular: ["group_by"],
            icon: "mdi-town-hall",
            extractFn: (entity) => entity.last_known_institutions,
        },
        {
            key: "last_known_institutions.country_code",
            entityToFilter: "authors",
            entityToSelect: "countries",
            type: "selectEntity",
            isCountry: true,
            category: "geo",
            actions: ["filter", "group_by"],
            actionsPopular: ["filter", "group_by"],
            icon: "mdi-earth",
            extractFn: (entity) => {
                if (!entity.last_known_institutions) return null;
                return entity.last_known_institutions.map(insti => {
                    const result = countryCodeLookup.byIso(insti.country_code);
                    return result?.country || insti.country_code;
                });
            },
            column: { export: { path: "last_known_institutions.country_code" } },
        },
        {
            key: "last_known_institutions.type",
            entityToFilter: "authors",
            entityToSelect: "institution-types",
            displayName: "Institution type",
            type: "selectEntity",
            category: "institution",
            actions: ["filter", "group_by"],
            actionsPopular: [],
            icon: "mdi-shape-outline",
        },
        {
            key: "has_orcid",
            entityToFilter: "authors",
            entityToSelect: "authors",
            type: "boolean",
            booleanValues: ["No ORCID", "Has ORCID"],
            actions: ["filter", "group_by"],
            actionsPopular: ["filter", "group_by"],
            category: "ids",
            icon: "mdi-tag-outline",
        },
        {
            key: "display_name_alternatives",
            entityToFilter: "authors",
            entityToSelect: "authors",
            type: "selectEntity",
            category: "other",
            actions: [],
            actionsPopular: [],
            icon: "mdi-town-hall",
            extractFn: (entity) => altNames(entity, "display_name_alternatives"),
            // The extracted items are plain name strings, not author objects —
            // a ":ids" sibling makes no sense here (would render an empty cell).
            noIdsSibling: true,
        },

        // authors: summary_stats
        {
            key: "summary_stats.h_index",
            entityToFilter: "authors",
            entityToSelect: "authors",
            type: "range",
            category: "citation",
            actions: ["filter", "sort"],   // header sort (oxjob #1658, ticket 23622)
            actionsPopular: ["filter"],
            icon: "mdi-chart-line",
            extractFn: (entity) => entity.summary_stats.h_index,
        },
        {
            key: "summary_stats.i10_index",
            entityToFilter: "authors",
            entityToSelect: "authors",
            type: "range",
            category: "citation",
            actions: ["filter", "sort"],   // header sort (oxjob #1658, ticket 23622)
            actionsPopular: [],
            icon: "mdi-chart-line",
            extractFn: (entity) => entity.summary_stats.i10_index,
        },
        {
            key: "summary_stats.2yr_mean_citedness",
            entityToFilter: "authors",
            entityToSelect: "authors",
            type: "range",
            category: "citation",
            actions: ["filter", "sort"],   // header sort (oxjob #1658, ticket 23622)
            actionsPopular: [],
            icon: "mdi-chart-line",
            extractFn: (entity) => entity.summary_stats?.['2yr_mean_citedness'],
        },

        // ============================================================
        // SOURCES
        // ============================================================
        {
            key: "ids.openalex",
            entityToFilter: "sources",
            entityToSelect: "sources",
            isId: true,
            type: "selectEntity",
            category: "ids",
            icon: "mdi-book-open-outline",
            actions: ["column"],
            // OWN_ID_COLUMN: the source's own OpenAlex ID (S…).
            extractFn: (entity) => entity.id,
            column: {
                label: "Source ID",
                render: { kind: "stringList", bareId: true },
                export: { recipe: "bare_openalex_id" },
            },
        },
        {
            key: "default.search",
            entityToFilter: "sources",
            entityToSelect: "sources",
            displayName: "name",
            type: "search",
            category: "search",
            actions: ["filter"],
            actionsPopular: ["filter"],
            icon: "mdi-magnify",
        },
        {
            key: "issn",
            entityToFilter: "sources",
            type: "search",
            isId: true,
            actions: ["filter", "column"],
            actionsPopular: [],
            category: "ids",
            icon: "mdi-book-open-outline",
            extractFn: (e) => e.ids?.issn,
            // type:"search" is a filter; column.render opts it in as a column.
            // Server flat-path is `issn` (the |-joined list).
            column: { render: { kind: "stringList" }, export: { path: "issn" } },
            noIdsSibling: true,
        },
        {
            key: "issn_l",
            entityToFilter: "sources",
            type: "search",
            isId: true,
            actions: ["column"],
            category: "ids",
            icon: "mdi-book-open-outline",
            extractFn: (e) => e.issn_l,
            column: { render: { kind: "text" } },
        },
        {
            key: "country_code",
            entityToFilter: "sources",
            entityToSelect: "countries",
            displayName: "Country",
            type: "selectEntity",
            isCountry: true,
            category: "geo",
            actions: ["filter"],
            actionsPopular: [],
            icon: "mdi-earth",
            extractFn: (entity) => {
                if (!entity.country_code) return null;
                const r = countryCodeLookup.byIso(entity.country_code);
                return r?.country || entity.country_code;
            },
            column: { export: { path: "country_code" } },
        },
        {
            key: "first_publication_year",
            entityToFilter: "sources",
            type: "range",
            category: "dates",
            actions: ["column"],
            icon: "mdi-calendar",
            extractFn: (entity) => entity.first_publication_year,
            column: { render: { kind: "year" } },
        },
        {
            key: "homepage_url",
            entityToFilter: "sources",
            type: "selectEntity",
            actions: [],
            category: "other",
            icon: "mdi-web",
            extractFn: (entity) => entity.homepage_url,
        },
        {
            key: "display_name.search",
            entityToFilter: "sources",
            displayName: "Title search",
            type: "search",
            category: "other",
            icon: "mdi-magnify",
        },
        {
            key: "display_name",
            isIdentityColumn: true,
            entityToFilter: "sources",
            type: "search",
            actions: ["sort", "column", "edit"],
            actionsPopular: ["sort", "column",],
            category: "other",
            icon: "mdi-account-outline",
            extractFn: (entity) => entity.display_name,
        },
        {
            key: "publisher",
            entityToFilter: "sources",
            entityToSelect: "publishers",
            displayName: "Publisher",
            type: "selectEntity",
            isManyOptions: true,
            category: "other",
            actions: ["edit"],
            actionsPopular: [],
            icon: "mdi-domain",
            extractFn: (e) => {
                if (e.type !== "journal") return
                return {
                    id: e.host_organization,
                    display_name: e.host_organization_name,
                }
            },
            // The "publisher" of a source is the API's `host_organization*` —
            // there's no `publisher.*` flat-path. Bare-ID variant reads
            // `host_organization` (the canonical URL; bare_openalex_id strips it).
            column: { export: { path: "host_organization_name", idsPath: "host_organization" } },
        },
        {
            key: "type",
            entityToFilter: "sources",
            entityToSelect: "source-types",
            displayName: "Source type",
            type: "selectEntity",
            category: "other",
            actions: ["filter", "edit"],
            actionsPopular: ["filter"],
            icon: "mdi-shape-outline",
            extractFn: (e) => e.type,
        },
        // oxjob #1526: publisher country, looked up in the publishers index (filter only).
        {
            key: "host_organization.country_code",
            entityToFilter: "sources",
            entityToSelect: "countries",
            displayName: "publisher country",
            type: "selectEntity",
            isManyOptions: true,
            isCountry: true,
            noGroupBy: true,
            category: "geo",
            actions: ["filter"],
            actionsPopular: [],
            icon: "mdi-earth",
        },
        {
            key: "topics.id",
            entityToFilter: "sources",
            entityToSelect: "topics",
            type: "selectEntity",
            isManyOptions: true,
            category: "aboutness",
            actions: ["filter", "group_by"],
            actionsPopular: ["filter", "group_by"],
            icon: "mdi-tag-outline",
        },
        // oxjob #1526: sources by subfield, like field and domain.
        {
            key: "topics.subfield.id",
            entityToFilter: "sources",
            entityToSelect: "subfields",
            displayName: "Subfield",
            type: "selectEntity",
            isManyOptions: true,
            category: "aboutness",
            actions: ["filter", "group_by"],
            actionsPopular: [],
            icon: "mdi-tag-outline",
        },
        {
            key: "topics.field.id",
            entityToFilter: "sources",
            entityToSelect: "fields",
            displayName: "Field",
            type: "selectEntity",
            isManyOptions: true,
            category: "aboutness",
            actions: ["filter", "group_by"],
            actionsPopular: [],
            icon: "mdi-tag-outline",
        },
        {
            key: "topics.domain.id",
            entityToFilter: "sources",
            entityToSelect: "domains",
            displayName: "Domain",
            type: "selectEntity",
            isManyOptions: true,
            category: "aboutness",
            actions: ["filter", "group_by"],
            actionsPopular: [],
            icon: "mdi-tag-outline",
        },
        {
            key: "apc_usd",
            entityToFilter: "sources",
            displayName: "Article Processing Charge",
            type: "range",
            category: "other",
            actions: ["filter", "edit"],
            actionsPopular: [],
            icon: "mdi-cash",
            extractFn: (e) => {
                if (!e.apc_usd) return
                return "$" + e.apc_usd.toLocaleString()
            },
        },
        {
            key: "is_oa",
            entityToFilter: "sources",
            type: "boolean",
            booleanValues: ["Not Open Access", "Open Access"],
            category: "open access",
            actions: ["filter", "edit"],
            actionsPopular: ["filter"],
            icon: "mdi-lock-open-outline",
            extractFn: (e) => e.is_oa,
        },
        {
            key: "is_in_doaj",
            entityToFilter: "sources",
            type: "boolean",
            booleanValues: ["Not in DOAJ", "In DOAJ"],
            category: "open access",
            actions: ["filter", "edit"],
            actionsPopular: ["filter"],
            icon: "mdi-lock-open-outline",
            extractFn: (entity) => entity.is_in_doaj,
        },
        {
            key: "is_core",
            entityToFilter: "sources",
            type: "boolean",
            booleanValues: ["Not a CWTS Core source", "CWTS Core source"],
            category: "other",
            actions: ["filter", "group_by"],
            actionsPopular: [],
            icon: "mdi-book-open-outline",
            extractFn: (entity) => entity.is_core,
        },
        {
            // External journal lists this source appears on (oxjob #1205). No
            // `entityToSelect`: options + counts come from group_by; labels from
            // src/listedIn.js. Non-normative: "appears on", never "recommended".
            key: "listed_in",
            entityToFilter: "sources",
            entityToSelect: "source-lists",
            displayName: "listed in",
            type: "selectEntity",
            category: "other",
            actions: ["filter", "column", "group_by"],
            actionsPopular: ["filter"],
            icon: "mdi-format-list-bulleted",
            extractFn: (entity) => (entity.listed_in || []).map(listedInLabel),
            column: { render: { kind: "stringList" }, export: { path: "listed_in" } },
        },
        {
            key: "alternate_titles",
            entityToFilter: "sources",
            type: "selectEntity",
            category: "other",
            actions: ["edit"],
            actionsPopular: [],
            icon: "mdi-book-open-outline",
            extractFn: (entity) => altNames(entity, "alternate_titles"),
        },

        // sources: summary_stats
        {
            key: "summary_stats.2yr_mean_citedness",
            entityToFilter: "sources",
            type: "range",
            category: "citation",
            actions: ["filter", "sort"],
            actionsPopular: ["sort"],
            icon: "mdi-chart-line",
            extractFn: (entity) => entity.summary_stats["2yr_mean_citedness"],
        },
        {
            key: "summary_stats.h_index",
            entityToFilter: "sources",
            type: "range",
            category: "citation",
            actions: ["filter"],
            actionsPopular: [],
            icon: "mdi-chart-line",
            extractFn: (entity) => entity.summary_stats.h_index,
        },
        {
            key: "summary_stats.i10_index",
            entityToFilter: "sources",
            type: "range",
            category: "citation",
            actions: ["filter"],
            actionsPopular: [],
            icon: "mdi-chart-line",
            extractFn: (entity) => entity.summary_stats.i10_index,
        },

        // ============================================================
        // PUBLISHERS
        // ============================================================
        {
            key: "ids.openalex",
            entityToFilter: "publishers",
            entityToSelect: "publishers",
            isId: true,
            type: "selectEntity",
            category: "ids",
            icon: "mdi-domain",
            actions: ["column"],
            // OWN_ID_COLUMN: the publisher's own OpenAlex ID (P…).
            extractFn: (entity) => entity.id,
            column: {
                label: "Publisher ID",
                render: { kind: "stringList", bareId: true },
                export: { recipe: "bare_openalex_id" },
            },
        },
        {
            key: "display_name.search",
            entityToFilter: "publishers",
            displayName: "Name search",
            type: "search",
            category: "other",
            icon: "mdi-magnify",
        },

        // ============================================================
        // FUNDERS
        // ============================================================
        {
            key: "ids.openalex",
            entityToFilter: "funders",
            entityToSelect: "funders",
            isId: true,
            type: "selectEntity",
            category: "ids",
            icon: "mdi-cash-multiple",
            actions: ["column"],
            // OWN_ID_COLUMN: the funder's own OpenAlex ID (F…).
            extractFn: (entity) => entity.id,
            column: {
                label: "Funder ID",
                render: { kind: "stringList", bareId: true },
                export: { recipe: "bare_openalex_id" },
            },
        },
        {
            key: "default.search",
            entityToFilter: "funders",
            entityToSelect: "funders",
            displayName: "name",
            type: "search",
            category: "search",
            actions: ["filter"],
            actionsPopular: ["filter"],
            icon: "mdi-magnify",
        },
        {
            key: "display_name",
            isIdentityColumn: true,
            entityToFilter: "funders",
            type: "search",
            actions: ["sort", "column",],
            actionsPopular: ["sort", "column",],
            category: "other",
            icon: "mdi-cash-multiple",
            extractFn: (entity) => entity.display_name,
        },
        {
            key: "ids.ror",
            entityToFilter: "funders",
            entityToSelect: "funders",
            isId: true,
            type: "selectEntity",
            category: "ids",
            icon: "mdi-cash-multiple",
            extractFn: (e) => e.ids?.ror,
        },
        {
            key: "ids.wikidata",
            entityToFilter: "funders",
            displayName: "Wikidata ID",
            isId: true,
            type: "selectEntity",
            category: "ids",
            actions: [],
            actionsPopular: [],
            icon: "mdi-web",
            extractFn: (e) => e.ids?.wikidata,
        },
        {
            key: "ids.crossref",
            entityToFilter: "funders",
            displayName: "Crossref ID",
            isId: true,
            type: "selectEntity",
            category: "ids",
            actions: [],
            actionsPopular: [],
            icon: "mdi-identifier",
            extractFn: (e) => e.ids?.crossref,
        },
        {
            key: "ids.doi",
            entityToFilter: "funders",
            isId: true,
            type: "selectEntity",
            category: "ids",
            actions: ["filter"],
            actionsPopular: [],
            icon: "mdi-identifier",
            extractFn: (e) => e.ids?.doi,
        },
        {
            key: "display_name.search",
            entityToFilter: "funders",
            displayName: "Name search",
            type: "search",
            category: "other",
            icon: "mdi-magnify",
        },
        {
            key: "country_code",
            entityToFilter: "funders",
            entityToSelect: "countries",
            displayName: "Country",
            type: "selectEntity",
            isManyOptions: true,
            isCountry: true,
            category: "geo",
            actions: ["filter"],
            actionsPopular: ["filter"],
            icon: "mdi-earth",
            extractFn: (entity) => entity.country_code,
        },
        {
            key: "is_global_south",
            entityToFilter: "funders",
            type: "boolean",
            actions: ["filter", "group_by"],
            actionsPopular: ["group_by"],
            category: "geo",
            booleanValues: ["Global North", "Global South"],
            icon: "mdi-earth",
            extractFn: (entity) => entity.is_global_south,
        },
        {
            key: "alternate_titles",
            entityToFilter: "funders",
            type: "selectEntity",
            category: "other",
            actions: [],
            actionsPopular: [],
            icon: "mdi-cash-multiple",
            extractFn: (entity) => altNames(entity, "alternate_titles"),
        },
        {
            key: "description",
            entityToFilter: "funders",
            type: "selectEntity",
            category: "other",
            actions: [],
            actionsPopular: [],
            icon: "mdi-text",
            extractFn: (entity) => entity.description,
        },
        {
            key: "homepage_url",
            entityToFilter: "funders",
            type: "selectEntity",
            category: "other",
            actions: [],
            actionsPopular: [],
            icon: "mdi-web",
            extractFn: (entity) => entity.homepage_url,
        },
        {
            key: "awards_count",
            entityToFilter: "funders",
            type: "range",
            category: "citation",
            actions: ["filter", "sort", "column"],
            actionsPopular: ["sort", "column"],
            icon: "mdi-cash-multiple",
            extractFn: (entity) => entity.awards_count,
        },
        // works_count / cited_by_count for funders are injected globally for
        // every entity (see worksCountFilters / citedByCountFilters below); the
        // per-entity copies that used to live here produced duplicate picker rows
        // (oxjob #621) — removed. Same trap called out in the #304 learning.
        {
            key: "summary_stats.2yr_mean_citedness",
            entityToFilter: "funders",
            type: "range",
            category: "citation",
            actions: ["filter", "sort", "column"],
            actionsPopular: [],
            icon: "mdi-chart-line",
            extractFn: (entity) => entity.summary_stats?.['2yr_mean_citedness'],
        },
        {
            key: "summary_stats.h_index",
            entityToFilter: "funders",
            type: "range",
            category: "citation",
            actions: ["filter", "sort", "column"],
            actionsPopular: [],
            icon: "mdi-chart-bar",
            extractFn: (entity) => entity.summary_stats?.h_index,
        },
        {
            key: "summary_stats.i10_index",
            entityToFilter: "funders",
            type: "range",
            category: "citation",
            actions: ["filter", "sort", "column"],
            actionsPopular: [],
            icon: "mdi-chart-bar",
            extractFn: (entity) => entity.summary_stats?.i10_index,
        },

        // ============================================================
        // INSTITUTIONS
        // ============================================================
        {
            key: "ids.openalex",
            entityToFilter: "institutions",
            entityToSelect: "institutions",
            isId: true,
            type: "selectEntity",
            category: "ids",
            icon: "mdi-town-hall",
            actions: ["column"],
            // OWN_ID_COLUMN: the institution's own OpenAlex ID (I…).
            extractFn: (entity) => entity.id,
            column: {
                label: "Institution ID",
                render: { kind: "stringList", bareId: true },
                export: { recipe: "bare_openalex_id" },
            },
        },
        {
            key: "default.search",
            entityToFilter: "institutions",
            entityToSelect: "institutions",
            displayName: "name",
            type: "search",
            category: "search",
            actions: ["filter"],
            actionsPopular: ["filter"],
            icon: "mdi-magnify",
        },
        {
            key: "display_name",
            isIdentityColumn: true,
            entityToFilter: "institutions",
            type: "search",
            actions: ["sort", "column",],
            actionsPopular: ["sort", "column",],
            category: "other",
            icon: "mdi-account-outline",
            extractFn: (entity) => entity.display_name,
        },
        {
            key: "homepage_url",
            entityToFilter: "institutions",
            type: "selectEntity",
            actions: [],
            category: "other",
            icon: "mdi-web",
            extractFn: (entity) => entity.homepage_url,
        },
        {
            key: "ids.ror",
            entityToFilter: "institutions",
            entityToSelect: "institutions",
            displayName: "ROR",
            isId: true,
            type: "selectEntity",
            category: "ids",
            icon: "mdi-town-hall",
            extractFn: (e) => e.ids.ror,
        },
        {
            key: "display_name.search",
            entityToFilter: "institutions",
            displayName: "Name search",
            type: "search",
            category: "other",
            icon: "mdi-magnify",
        },
        {
            key: "country_code",
            entityToFilter: "institutions",
            entityToSelect: "countries",
            displayName: "Country",
            type: "selectEntity",
            isManyOptions: true,
            isCountry: true,
            category: "geo",
            actions: ["filter"],
            actionsPopular: ["filter"],
            icon: "mdi-earth",
            // Column shows the country NAME (codebook lookup); CSV ships the raw
            // ISO code at the `country_code` flat-path (accepted CODE_VS_NAME).
            extractFn: (entity) => {
                if (!entity.country_code) return null;
                const r = countryCodeLookup.byIso(entity.country_code);
                return r?.country || entity.country_code;
            },
            column: { export: { path: "country_code" } },
        },
        {
            key: "type",
            entityToFilter: "institutions",
            entityToSelect: "institution-types",
            displayName: "Institution type",
            type: "selectEntity",
            category: "other",
            actions: ["filter", "edit"],
            actionsPopular: ["filter"],
            icon: "mdi-shape-outline",
            extractFn: (entity) => entity.type,
        },
        {
            key: "x_concepts.id",
            entityToFilter: "institutions",
            displayName: "Concepts",
            entityToSelect: "concepts",
            type: "selectEntity",
            isManyOptions: true,
            category: "other",
            icon: "mdi-tag-outline",
        },
        {
            key: "display_name_alternatives",
            entityToFilter: "institutions",
            type: "selectEntity",
            category: "other",
            actions: [],
            actionsPopular: [],
            icon: "mdi-town-hall",
            extractFn: (entity) => altNames(entity, "display_name_alternatives"),
            noIdsSibling: true,
        },
        {
            key: "parent_institutions",
            entityToFilter: "institutions",
            displayName: "parent institutions",
            type: "selectEntity",
            category: "other",
            actions: [],
            actionsPopular: [],
            icon: "mdi-town-hall",
            extractFn: (entity) => entity.associated_institutions.filter(i => {
                return i.relationship === "parent"
            }),
            // The export service pre-splits associated_institutions by
            // relationship into virtual {rel}_institutions fields
            // (openalex-users-api formats/csv_manifest.py, ZD 23586), so the
            // CSV column carries exactly what the table shows.
            column: { export: { path: "parent_institutions.display_name", idsPath: "parent_institutions.id" } },
        },
        {
            key: "child_institutions",
            entityToFilter: "institutions",
            displayName: "child institutions",
            type: "selectEntity",
            category: "other",
            actions: [],
            actionsPopular: [],
            icon: "mdi-town-hall",
            extractFn: (entity) => (entity.associated_institutions ?? []).filter(i => {
                return i.relationship === "child"
            }),
            // Relationship-true export path: the export service pre-splits
            // associated_institutions into virtual {rel}_institutions fields
            // (openalex-users-api formats/csv_manifest.py, ZD 23586). The old
            // superset path shipped parents/predecessors under this header.
            column: { export: { path: "child_institutions.display_name", idsPath: "child_institutions.id" } },
        },
        {
            key: "related_institutions",
            entityToFilter: "institutions",
            displayName: "related institutions",
            type: "selectEntity",
            category: "other",
            actions: [],
            actionsPopular: [],
            icon: "mdi-town-hall",
            extractFn: (entity) => (entity.associated_institutions ?? []).filter(i => {
                return i.relationship === "related"
            }),
            // See `child_institutions` — same relationship-true export path.
            column: { export: { path: "related_institutions.display_name", idsPath: "related_institutions.id" } },
        },
        {
            key: "lineage",
            entityToFilter: "institutions",
            entityToSelect: "institutions",
            type: "selectEntity",
            // Needs a category in facetCategories.institutions or facetsByCategory
            // drops it from the picker (oxjob #621; the #304 "column-eligible yet
            // absent" trap). "other" is in that entity's list.
            category: "other",
            actions: ["filter"],
            actionsPopular: [],
            icon: "mdi-town-hall",
        },
        // institutions: geo. Exact-value filters since oxjob #1526 (`search`
        // type = typed value, like ORCID); also offered as columns.
        // flat-paths: geo.city, geo.region.
        {
            key: "geo.city",
            entityToFilter: "institutions",
            displayName: "city",
            type: "search",
            quoteValue: true,
            category: "geo",
            actions: ["filter", "column"],
            actionsPopular: [],
            icon: "mdi-map-marker-outline",
            extractFn: (entity) => entity.geo?.city,
        },
        {
            key: "geo.region",
            entityToFilter: "institutions",
            displayName: "region",
            type: "search",
            quoteValue: true,
            category: "geo",
            actions: ["filter", "column"],
            actionsPopular: [],
            icon: "mdi-map-marker-outline",
            extractFn: (entity) => entity.geo?.region,
        },
        {
            key: "display_name_acronyms",
            entityToFilter: "institutions",
            type: "selectEntity",
            category: "other",
            actions: ["column"],
            actionsPopular: [],
            icon: "mdi-town-hall",
            extractFn: (entity) => entity.display_name_acronyms,
            // Plain strings (UM, UMich…), not entity objects.
            noIdsSibling: true,
            column: { render: { kind: "stringList" } },
        },
        // institutions: summary_stats
        {
            key: "summary_stats.2yr_mean_citedness",
            entityToFilter: "institutions",
            type: "range",
            category: "citation",
            actions: ["filter", "sort", "column"],
            actionsPopular: [],
            icon: "mdi-chart-line",
            extractFn: (entity) => entity.summary_stats?.['2yr_mean_citedness'],
        },
        {
            key: "summary_stats.h_index",
            entityToFilter: "institutions",
            type: "range",
            category: "citation",
            actions: ["filter", "sort", "column"],
            actionsPopular: [],
            icon: "mdi-chart-bar",
            extractFn: (entity) => entity.summary_stats?.h_index,
        },
        {
            key: "summary_stats.i10_index",
            entityToFilter: "institutions",
            type: "range",
            category: "citation",
            actions: ["filter", "sort", "column"],
            actionsPopular: [],
            icon: "mdi-chart-bar",
            extractFn: (entity) => entity.summary_stats?.i10_index,
        },

        // ============================================================
        // CONCEPTS
        // ============================================================
        {
            key: "ids.openalex",
            entityToFilter: "concepts",
            entityToSelect: "concepts",
            isId: true,
            type: "selectEntity",
            category: "ids",
            icon: "mdi-tag-outline",
            actions: ["column"],
            // OWN_ID_COLUMN: the concept's own OpenAlex ID (C…).
            extractFn: (entity) => entity.id,
            column: {
                label: "Concept ID",
                render: { kind: "stringList", bareId: true },
                export: { recipe: "bare_openalex_id" },
            },
        },
        {
            key: "display_name.search",
            entityToFilter: "concepts",
            displayName: "Name search",
            type: "search",
            category: "other",
            icon: "mdi-magnify",
        },
        {
            key: "display_name",
            isIdentityColumn: true,
            entityToFilter: "concepts",
            type: "search",
            actions: ["sort", "column"],
            actionsPopular: ["sort", "column"],
            category: "other",
            icon: "mdi-tag-outline",
            extractFn: (entity) => entity.display_name,
        },
        {
            key: "description",
            entityToFilter: "concepts",
            type: "search",
            category: "other",
            actions: ["column"],
            icon: "mdi-text",
            extractFn: (entity) => entity.description,
            column: { render: { kind: "text" } },
        },
        {
            key: "level",
            entityToFilter: "concepts",
            displayName: "Level",
            type: "selectEntity",
            category: "other",
            actions: ["column"],
            icon: "mdi-tag-outline",
            extractFn: (entity) => entity.level,
            column: { render: { kind: "number" } },
        },

        // ============================================================
        // TOPICS
        // ============================================================
        {
            key: "ids.openalex",
            entityToFilter: "topics",
            entityToSelect: "topics",
            isId: true,
            type: "selectEntity",
            category: "ids",
            icon: "mdi-tag-outline",
            actions: ["column"],
            // OWN_ID_COLUMN: the topic's own OpenAlex ID (T…).
            extractFn: (entity) => entity.id,
            column: {
                label: "Topic ID",
                render: { kind: "stringList", bareId: true },
                export: { recipe: "bare_openalex_id" },
            },
        },
        {
            key: "description",
            entityToFilter: "topics",
            entityToSelect: "topics",
            type: "search",
            category: "other",
            actions: ["column"],
            icon: "mdi-tag-outline",
            extractFn: (e) => e.description,
            // type:"search" stays (it's a filter); the column.render block makes
            // it ALSO a column (deriveColumnRender checks column.render first).
            column: { render: { kind: "text" } },
        },
        {
            key: "keywords",
            entityToFilter: "topics",
            type: "text",
            category: "other",
            actions: ["column"],
            actionsPopular: [],
            icon: "mdi-tag-outline",
            // oxjob #1307: the topic's characteristic keywords {id, display_name, score}: >= 20% of each keyword's
            // works fall in this topic; the 25 on the most of the topic's works.
            displayName: "characteristic keywords",
            extractFn: (entity) => entity.keywords,
            noIdsSibling: true,
            column: { render: { kind: "entityList", itemLabelField: "display_name", itemLinkField: "id" } },
        },
        {
            key: "siblings",
            entityToFilter: "topics",
            entityToSelect: "topics",
            type: "selectEntity",
            actions: [],  // #294: no server `siblings` filter; entity-page display only
            category: "other",
            icon: "mdi-tag-outline",
            extractFn: (e) => e.siblings,
        },
        {
            key: "subfield.id",
            entityToFilter: "topics",
            entityToSelect: "topics",
            type: "selectEntity",
            actions: ["filter", "group_by"],
            category: "other",
            icon: "mdi-tag-outline",
            extractFn: (e) => e.subfield,
        },
        {
            key: "field.id",
            entityToFilter: "topics",
            entityToSelect: "topics",
            type: "selectEntity",
            actions: ["filter", "group_by"],
            category: "other",
            icon: "mdi-tag-outline",
            extractFn: (e) => e.field,
        },
        {
            key: "domain.id",
            entityToFilter: "topics",
            entityToSelect: "topics",
            type: "selectEntity",
            actions: ["filter", "group_by"],
            category: "other",
            icon: "mdi-tag-outline",
            extractFn: (e) => e.domain,
        },

        // ============================================================
        // SUBFIELDS
        // ============================================================
        {
            key: "description",
            entityToFilter: "subfields",
            entityToSelect: "subfields",
            type: "search",
            category: "other",
            icon: "mdi-tag-outline",
            extractFn: (e) => e.description,
        },
        {
            key: "display_name_alternatives",
            entityToFilter: "subfields",
            entityToSelect: "subfields",
            type: "search",
            category: "other",
            icon: "mdi-tag-outline",
            extractFn: (e) => altNames(e, "display_name_alternatives"),
        },
        {
            key: "topics",
            entityToFilter: "subfields",
            entityToSelect: "subfields",
            type: "selectEntity",
            actions: [],  // #294: no server `topics` filter on /subfields; entity-page display only
            category: "other",
            icon: "mdi-tag-outline",
            extractFn: (e) => e.topics,
        },
        {
            key: "siblings",
            entityToFilter: "subfields",
            entityToSelect: "subfields",
            type: "selectEntity",
            actions: [],  // #294: no server `siblings` filter; entity-page display only
            category: "other",
            icon: "mdi-tag-outline",
            extractFn: (e) => e.siblings,
        },
        {
            key: "field.id",
            entityToFilter: "subfields",
            entityToSelect: "subfields",
            type: "selectEntity",
            actions: ["filter", "group_by"],
            category: "other",
            icon: "mdi-tag-outline",
            extractFn: (e) => e.field,
        },
        {
            key: "domain.id",
            entityToFilter: "subfields",
            entityToSelect: "subfields",
            type: "selectEntity",
            actions: ["filter", "group_by"],
            category: "other",
            icon: "mdi-tag-outline",
            extractFn: (e) => e.domain,
        },

        // ============================================================
        // FIELDS
        // ============================================================
        {
            key: "description",
            entityToFilter: "fields",
            entityToSelect: "fields",
            type: "search",
            category: "other",
            icon: "mdi-tag-outline",
            extractFn: (e) => e.description,
        },
        {
            key: "display_name_alternatives",
            entityToFilter: "fields",
            entityToSelect: "fields",
            type: "search",
            category: "other",
            icon: "mdi-tag-outline",
            extractFn: (e) => altNames(e, "display_name_alternatives"),
        },
        {
            key: "siblings",
            entityToFilter: "fields",
            entityToSelect: "fields",
            type: "selectEntity",
            actions: [],  // #294: no server `siblings` filter; entity-page display only
            category: "other",
            icon: "mdi-tag-outline",
            extractFn: (e) => e.siblings,
        },
        {
            key: "subfields",
            entityToFilter: "fields",
            entityToSelect: "fields",
            type: "selectEntity",
            actions: [],  // #294: no server `subfields` filter on /fields; entity-page display only
            category: "other",
            icon: "mdi-tag-outline",
            extractFn: (e) => e.subfields,
        },
        {
            key: "domain.id",
            entityToFilter: "fields",
            entityToSelect: "fields",
            type: "selectEntity",
            actions: ["filter", "group_by"],
            category: "other",
            icon: "mdi-tag-outline",
            extractFn: (e) => e.domain,
        },

        // ============================================================
        // DOMAINS
        // ============================================================
        {
            key: "description",
            entityToFilter: "domains",
            entityToSelect: "domains",
            type: "search",
            category: "other",
            icon: "mdi-tag-outline",
            extractFn: (e) => e.description,
        },
        {
            key: "display_name_alternatives",
            entityToFilter: "domains",
            entityToSelect: "domains",
            type: "search",
            category: "other",
            icon: "mdi-tag-outline",
            extractFn: (e) => altNames(e, "display_name_alternatives"),
        },
        {
            key: "fields",
            entityToFilter: "domains",
            entityToSelect: "domains",
            type: "selectEntity",
            category: "other",
            icon: "mdi-tag-outline",
            extractFn: (e) => e.fields,
        },
        {
            key: "siblings",
            entityToFilter: "domains",
            entityToSelect: "domains",
            type: "selectEntity",
            category: "other",
            icon: "mdi-tag-outline",
            extractFn: (e) => e.siblings,
        },

        // ============================================================
        // TYPES
        // ============================================================
        {
            key: "description",
            entityToFilter: "types",
            entityToSelect: "types",
            type: "search",
            category: "other",
            icon: "mdi-shape-outline",
            extractFn: (e) => e.description,
        },
        {
            key: "crossref_types",
            entityToFilter: "types",
            entityToSelect: "types",
            displayName: "crossref types",
            type: "selectEntity",
            category: "other",
            icon: "mdi-shape-outline",
            extractFn: (e) => e.crossref_types,
        },

        // ============================================================
        // CONTINENTS
        // ============================================================
        {
            key: "countries",
            entityToFilter: "continents",
            entityToSelect: "continents",
            type: "selectEntity",
            category: "other",
            icon: "mdi-earth",
            extractFn: (e) => e.countries,
        },

        // ============================================================
        // LOCATIONS
        // ============================================================
        {
            key: "work_id",
            entityToFilter: "locations",
            entityToSelect: "works",
            type: "selectEntity",
            isManyOptions: true,
            category: "ids",
            actions: ["filter", "column"],
            actionsPopular: [],
            icon: "mdi-file-document-outline",
            // Entity page synthesizes a full `work` object; raw SERP rows only
            // carry work_id — fall back to a minimal one.
            extractFn: (e) => e.work
                ?? (e.work_id ? { id: e.work_id, display_name: e.work_id } : null),
        },
        {
            key: "landing_page_url",
            entityToFilter: "locations",
            type: "search",
            category: "other",
            icon: "mdi-link",
            extractFn: (e) => e.landing_page_url,
        },
        {
            key: "pdf_url",
            entityToFilter: "locations",
            type: "search",
            category: "other",
            icon: "mdi-file-pdf-box",
            extractFn: (e) => e.pdf_url,
        },
        {
            // The repository's own record id (exact match server-side —
            // TermField; the driving "paste your record ID" audit affordance).
            key: "native_id",
            entityToFilter: "locations",
            type: "search",
            category: "ids",
            actions: ["filter", "column"],
            actionsPopular: [],
            column: { render: { kind: "text" } },
            icon: "mdi-identifier",
            extractFn: (e) => e.native_id,
        },
        {
            // Closed vocab (doi/pmh/mag/pmid/…) — selectEntity with no
            // entityToSelect picks values via group_by (funding_type pattern).
            key: "native_id_namespace",
            entityToFilter: "locations",
            type: "selectEntity",
            category: "ids",
            actions: ["filter", "group_by", "column"],
            actionsPopular: [],
            icon: "mdi-tag-outline",
            extractFn: (e) => e.native_id_namespace,
        },
        {
            key: "id",
            entityToFilter: "locations",
            type: "search",
            category: "ids",
            icon: "mdi-identifier",
            extractFn: (e) => e.id,
        },
        {
            // Closed vocab (which ingest pipeline minted the location) —
            // group_by-picked, funding_type pattern.
            key: "provenance",
            entityToFilter: "locations",
            type: "selectEntity",
            category: "other",
            actions: ["filter", "group_by", "column"],
            actionsPopular: [],
            icon: "mdi-source-branch",
            extractFn: (e) => e.provenance,
        },
        {
            // The location's harvested title — the identity column on the
            // locations SERP table (api.js copies title -> display_name at the
            // boundary). `search=` is a server no-op until #915's v3 reindex
            // ships the analyzed title mapping (#850), so no filter action yet.
            key: "title",
            entityToFilter: "locations",
            type: "search",
            category: "other",
            actions: ["column"],
            actionsPopular: [],
            isIdentityColumn: true,
            icon: "mdi-text",
            extractFn: (e) => e.title,
        },
        {
            // Closed vocab (article/preprint/…) — group_by-picked.
            key: "type",
            entityToFilter: "locations",
            type: "selectEntity",
            category: "other",
            actions: ["filter", "group_by", "column"],
            actionsPopular: [],
            icon: "mdi-shape-outline",
            extractFn: (e) => e.type,
        },
        {
            key: "source_name",
            entityToFilter: "locations",
            type: "search",
            category: "source",
            icon: "mdi-book-open-outline",
            extractFn: (e) => e.source_name,
        },
        {
            key: "publisher",
            entityToFilter: "locations",
            type: "search",
            category: "source",
            icon: "mdi-domain",
            extractFn: (e) => e.publisher,
        },
        {
            // THE anchor filter for the driving use case: a repository manager
            // scoping the SERP to their own source (oxjob #852).
            key: "source_id",
            entityToFilter: "locations",
            entityToSelect: "sources",
            type: "selectEntity",
            isManyOptions: true,
            category: "source",
            actions: ["filter", "group_by", "column"],
            actionsPopular: ["filter"],
            icon: "mdi-book-open-outline",
            // Entity page synthesizes a full `source` object; raw SERP rows
            // only carry source_id/source_name — fall back to a minimal one
            // (bare S-id as the label when the row has no source_name).
            extractFn: (e) => e.source
                ?? (e.source_id ? {
                    id: e.source_id,
                    display_name: e.source_name
                        || String(e.source_id).replace(/^https?:\/\/openalex\.org\//, ''),
                } : null),
        },
        {
            key: "is_oa",
            entityToFilter: "locations",
            type: "boolean",
            category: "open access",
            // #852 flipped the locations facets from extract-only to real
            // actions (was: only is_oa, per #621's OQL-round-trip caveat).
            // The server-side OQL column-resolution fixes for the other
            // locations fields belong to #850; until those land, these facets
            // stay dark behind entityConfigs `hasSerp: false`. The flips are
            // also the input that populates `supported_by` (#420: GUI client
            // registry -> elastic-api vendored snapshot -> catalog).
            actions: ["filter", "group_by", "column"],
            actionsPopular: ["filter"],
            icon: "mdi-lock-open-outline",
            extractFn: (e) => e.is_oa,
        },
        {
            // Closed vocab (submittedVersion/acceptedVersion/publishedVersion)
            // — group_by-picked. NOTE: group_by=version returns all-zero
            // buckets until #850's works-override fix lands server-side.
            key: "version",
            entityToFilter: "locations",
            type: "selectEntity",
            category: "open access",
            actions: ["filter", "group_by", "column"],
            actionsPopular: ["filter"],
            icon: "mdi-tag-outline",
            extractFn: (e) => e.version,
        },
        {
            key: "license",
            entityToFilter: "locations",
            entityToSelect: "licenses",
            type: "selectEntity",
            category: "open access",
            actions: ["filter", "group_by", "column"],
            actionsPopular: ["filter"],
            icon: "mdi-lock-open-outline",
            extractFn: (e) => e.license,
        },
        {
            key: "language",
            entityToFilter: "locations",
            entityToSelect: "languages",
            type: "selectEntity",
            category: "other",
            actions: ["filter", "group_by", "column"],
            actionsPopular: [],
            icon: "mdi-translate",
            extractFn: (e) => e.language,
        },

        // ============================================================
        // AWARDS
        // ============================================================
        // oxjob #1526: funder country, looked up in the funders index (filter only).
        {
            key: "funder.country_code",
            entityToFilter: "awards",
            entityToSelect: "countries",
            displayName: "funder country",
            type: "selectEntity",
            isManyOptions: true,
            isCountry: true,
            noGroupBy: true,
            category: "funder",
            actions: ["filter"],
            actionsPopular: [],
            icon: "mdi-earth",
        },
        {
            key: "primary_topic.id",
            entityToFilter: "awards",
            entityToSelect: "topics",
            type: "selectEntity",
            isManyOptions: true,
            category: "aboutness",
            actions: ["filter", "group_by"],
            actionsPopular: ["filter", "group_by"],
            icon: "mdi-tag-outline",
            extractFn: (entity) => entity.primary_topic,
        },
        {
            key: "primary_topic.subfield.id",
            entityToFilter: "awards",
            entityToSelect: "subfields",
            type: "selectEntity",
            isManyOptions: true,
            category: "aboutness",
            actions: ["filter", "group_by"],
            actionsPopular: [],
            icon: "mdi-tag-outline",
            extractFn: (entity) => entity.primary_topic?.subfield,
        },
        {
            key: "primary_topic.field.id",
            entityToFilter: "awards",
            entityToSelect: "fields",
            type: "selectEntity",
            isManyOptions: true,
            category: "aboutness",
            actions: ["filter", "group_by"],
            actionsPopular: [],
            icon: "mdi-tag-outline",
            extractFn: (entity) => entity.primary_topic?.field,
        },
        {
            key: "primary_topic.domain.id",
            entityToFilter: "awards",
            entityToSelect: "domains",
            type: "selectEntity",
            isManyOptions: true,
            category: "aboutness",
            actions: ["filter", "group_by"],
            actionsPopular: [],
            icon: "mdi-tag-outline",
            extractFn: (entity) => entity.primary_topic?.domain,
        },
        {
            key: "institution_awarded.lineage",
            entityToFilter: "awards",
            entityToSelect: "institutions",
            type: "selectEntity",
            isManyOptions: true,
            category: "institution",
            actions: ["filter", "group_by"],
            actionsPopular: ["filter", "group_by"],
            icon: "mdi-town-hall",
            extractFn: (entity) => entity.institution_awarded,
        },
        {
            key: "institution_awarded.country_code",
            entityToFilter: "awards",
            entityToSelect: "countries",
            type: "selectEntity",
            isCountry: true,
            category: "geo",
            actions: ["filter", "group_by"],
            actionsPopular: ["filter", "group_by"],
            icon: "mdi-earth",
            extractFn: (entity) => entity.institution_awarded?.country_code,
        },
        {
            key: "display_name",
            isIdentityColumn: true,
            entityToFilter: "awards",
            type: "search",
            actions: [ "column"],
            actionsPopular: [ "column"],
            category: "other",
            icon: "mdi-file-document-outline",
            extractFn: (entity) => entity.display_name,
        },
        {
            key: "amount",
            entityToFilter: "awards",
            type: "range",
            actions: ["sort", "column", "filter"],
            actionsPopular: ["sort", "column"],
            category: "other",
            icon: "mdi-cash",
            extractFn: (entity) => entity.amount,
        },
        {
            key: "funder.id",
            entityToFilter: "awards",
            entityToSelect: "funders",
            type: "selectEntity",
            isManyOptions: true,
            category: "funder",
            actions: ["filter", "column", "group_by"],
            actionsPopular: ["filter", "group_by"],
            icon: "mdi-cash-multiple",
            extractFn: (entity) => entity.funder,
        },
        {
            key: "funding_type",
            entityToFilter: "awards",
            type: "selectEntity",
            category: "other",
            actions: ["filter", "column", "group_by"],
            actionsPopular: ["filter", "group_by"],
            icon: "mdi-tag-outline",
            extractFn: (entity) => entity.funding_type,
        },
        {
            // #294: server has no `start_date` filter (only `start_year`); was a broken
            // filter chip. Keep as display column; year filtering moved to `start_year`.
            key: "start_date",
            entityToFilter: "awards",
            type: "range",
            category: "dates",
            actions: ["column"],
            actionsPopular: [],
            icon: "mdi-calendar-start",
            extractFn: (entity) => entity.start_date,
        },
        {
            // #294: server has no `end_date` filter (only `end_year`); was a broken
            // filter chip. Keep as display column; year filtering moved to `end_year`.
            key: "end_date",
            entityToFilter: "awards",
            type: "range",
            category: "dates",
            actions: ["column"],
            actionsPopular: [],
            icon: "mdi-calendar-end",
            extractFn: (entity) => entity.end_date,
        },
        {
            key: "funded_outputs_count",
            entityToFilter: "awards",
            type: "range",
            category: "other",
            actions: ["filter", "sort", "column"],
            actionsPopular: ["sort", "column"],
            icon: "mdi-file-document-multiple-outline",
            extractFn: (entity) => entity.funded_outputs_count,
        },
        {
            // #294 server-supported year-range filter (RangeField start_year).
            // Single source for the awards start-year facet (oxjob #621 removed a
            // duplicate simpler copy that produced two picker rows).
            key: "start_year",
            entityToFilter: "awards",
            isDate: true,
            type: "range",
            category: "dates",
            actions: ["filter", "sort", "column", "group_by"],
            actionsPopular: ["sort", "column", "group_by"],
            icon: "mdi-calendar-start",
            extractFn: (entity) => entity.start_year,
        },
        {
            // #294 server-supported year-range filter (RangeField end_year).
            key: "end_year",
            entityToFilter: "awards",
            isDate: true,
            type: "range",
            category: "dates",
            actions: ["filter", "sort", "column"],
            actionsPopular: [],
            icon: "mdi-calendar-end",
            extractFn: (entity) => entity.end_year,
        },
        {
            key: "currency",
            entityToFilter: "awards",
            type: "selectEntity",
            category: "other",
            actions: [],
            actionsPopular: [],
            icon: "mdi-currency-usd",
        },
        {
            key: "doi",
            entityToFilter: "awards",
            type: "search",
            category: "ids",
            actions: ["filter", "column"],
            actionsPopular: [],
            icon: "mdi-identifier",
            extractFn: (entity) => entity.doi,
        },
        {
            key: "id",
            entityToFilter: "awards",
            type: "search",
            category: "ids",
            actions: ["filter"],
            actionsPopular: [],
            icon: "mdi-identifier",
        },
        {
            key: "funder_award_id",
            entityToFilter: "awards",
            type: "search",
            category: "ids",
            actions: ["filter", "column"],
            actionsPopular: [],
            icon: "mdi-identifier",
            extractFn: (entity) => entity.funder_award_id,
        },
        {
            key: "funder.doi",
            entityToFilter: "awards",
            type: "search",
            category: "funder",
            actions: ["filter"],
            actionsPopular: [],
            icon: "mdi-identifier",
        },
        {
            key: "funder.ror",
            entityToFilter: "awards",
            type: "search",
            category: "funder",
            actions: ["filter"],
            actionsPopular: [],
            icon: "mdi-identifier",
        },
        
        
        {
            key: "lead_investigator.affiliation.country",
            entityToFilter: "awards",
            entityToSelect: "countries",
            type: "selectEntity",
            isManyOptions: true,
            isCountry: true,
            category: "investigator",
            actions: [],
            actionsPopular: [],
            icon: "mdi-earth",
        },
        {
            key: "lead_investigator.affiliation.name",
            entityToFilter: "awards",
            type: "search",
            isManyOptions: true,
            category: "investigator",
            actions: [],
            actionsPopular: [],
            icon: "mdi-domain",
            extractFn: (entity) => entity.lead_investigator?.affiliation?.name,
        },
        {
            key: "investigators",
            entityToFilter: "awards",
            type: "selectEntity",
            isManyOptions: true,
            category: "investigator",
            actions: ["column"],
            actionsPopular: [],
            icon: "mdi-account-outline",
            extractFn: (entity) => {
                // Combine lead_investigator, co_lead_investigator, and investigators array
                // Return plain strings (no links) since investigators aren't disambiguated
                const investigators = [];
                const addInvestigator = (inv) => {
                    const name = [inv.given_name, inv.family_name].filter(Boolean).join(' ');
                    if (name) investigators.push(name);
                };
                if (entity.lead_investigator) addInvestigator(entity.lead_investigator);
                if (entity.co_lead_investigator) addInvestigator(entity.co_lead_investigator);
                if (entity.investigators && Array.isArray(entity.investigators)) {
                    entity.investigators.forEach(addInvestigator);
                }
                return investigators;
            },
        },
        {
            key: "investigators.affiliation",
            entityToFilter: "awards",
            displayName: "institution",
            type: "selectEntity",
            isManyOptions: true,
            category: "investigator",
            actions: ["column"],
            actionsPopular: [],
            icon: "mdi-town-hall",
            extractFn: (entity) => {
                // Collect all unique affiliations from all investigators
                // Return plain strings since affiliations aren't linked to institution entities
                const seen = new Set();
                const addAffiliation = (inv) => {
                    if (inv?.affiliation?.name && !seen.has(inv.affiliation.name)) {
                        seen.add(inv.affiliation.name);
                    }
                };
                if (entity.lead_investigator) addAffiliation(entity.lead_investigator);
                if (entity.co_lead_investigator) addAffiliation(entity.co_lead_investigator);
                if (entity.investigators && Array.isArray(entity.investigators)) {
                    entity.investigators.forEach(addAffiliation);
                }
                return Array.from(seen);
            },
        },
        {
            key: "provenance",
            entityToFilter: "awards",
            type: "selectEntity",
            category: "other",
            actions: ["filter", "group_by"],
            actionsPopular: [],
            icon: "mdi-source-branch",
            extractFn: (entity) => entity.provenance,
        },
        {
            key: "description",
            entityToFilter: "awards",
            type: "text",
            category: "other",
            actions: ["column"],
            actionsPopular: [],
            icon: "mdi-text",
            extractFn: (entity) => entity.description,
        },
        {
            key: "landing_page_url",
            entityToFilter: "awards",
            type: "url",
            category: "ids",
            actions: ["column"],
            actionsPopular: [],
            icon: "mdi-link",
            extractFn: (entity) => entity.landing_page_url,
        },
        {
            key: "funder_scheme",
            entityToFilter: "awards",
            type: "selectEntity",
            isManyOptions: true,
            category: "funder",
            actions: ["filter", "column", "group_by"],
            actionsPopular: [],
            icon: "mdi-tag-outline",
            extractFn: (entity) => entity.funder_scheme,
        },

        // ============================================================
        // PUBLISHERS (entity page rows)
        // ============================================================
        {
            key: "alternate_titles",
            entityToFilter: "publishers",
            type: "selectEntity",
            actions: [],
            category: "other",
            icon: "mdi-domain",
            extractFn: (entity) => altNames(entity, "alternate_titles"),
        },
        {
            key: "parent_publisher",
            entityToFilter: "publishers",
            type: "selectEntity",
            actions: [],
            category: "other",
            icon: "mdi-domain",
            extractFn: (entity) => entity.parent_publisher,
        },
        {
            key: "country_codes",
            entityToFilter: "publishers",
            type: "selectEntity",
            actions: [],
            category: "other",
            icon: "mdi-earth",
            extractFn: (entity) => {
                if (!entity.country_codes) return null;
                return entity.country_codes.map(code => {
                    const result = countryCodeLookup.byIso(code);
                    return result?.country || code;
                });
            },
        },
        {
            key: "homepage_url",
            entityToFilter: "publishers",
            type: "selectEntity",
            actions: [],
            category: "other",
            icon: "mdi-web",
            extractFn: (entity) => entity.homepage_url,
        },
        {
            key: "ids.ror",
            entityToFilter: "publishers",
            isId: true,
            type: "selectEntity",
            category: "ids",
            actions: [],
            icon: "mdi-domain",
            extractFn: (e) => e.ids?.ror,
        },
        {
            key: "ids.wikidata",
            entityToFilter: "publishers",
            displayName: "Wikidata ID",
            isId: true,
            type: "selectEntity",
            category: "ids",
            actions: [],
            icon: "mdi-web",
            extractFn: (e) => e.ids?.wikidata,
        },
        {
            key: "hierarchy_level",
            entityToFilter: "publishers",
            type: "range",
            category: "other",
            actions: [],
            icon: "mdi-file-tree",
            extractFn: (entity) => entity.hierarchy_level,
        },
        {
            key: "summary_stats.2yr_mean_citedness",
            entityToFilter: "publishers",
            type: "range",
            category: "citation",
            actions: ["filter"],
            icon: "mdi-chart-line",
            extractFn: (entity) => entity.summary_stats?.['2yr_mean_citedness'],
        },
        {
            key: "summary_stats.h_index",
            entityToFilter: "publishers",
            type: "range",
            category: "citation",
            actions: ["filter"],
            icon: "mdi-chart-bar",
            extractFn: (entity) => entity.summary_stats?.h_index,
        },
        {
            key: "summary_stats.i10_index",
            entityToFilter: "publishers",
            type: "range",
            category: "citation",
            actions: ["filter"],
            icon: "mdi-chart-bar",
            extractFn: (entity) => entity.summary_stats?.i10_index,
        },

        // ============================================================
        // COUNTRIES (entity page rows)
        // ============================================================
        {
            key: "description",
            entityToFilter: "countries",
            type: "search",
            category: "other",
            icon: "mdi-earth",
            extractFn: (e) => e.description,
        },
        {
            key: "display_name_alternatives",
            entityToFilter: "countries",
            type: "search",
            category: "other",
            icon: "mdi-earth",
            extractFn: (e) => altNames(e, "display_name_alternatives"),
        },
        {
            key: "continent",
            entityToFilter: "countries",
            type: "selectEntity",
            actions: [],
            category: "other",
            icon: "mdi-earth",
            extractFn: (e) => e.continent,
        },
        {
            key: "is_global_south",
            entityToFilter: "countries",
            type: "boolean",
            actions: [],
            category: "other",
            icon: "mdi-earth",
            extractFn: (e) => e.is_global_south,
        },

        // ============================================================
        // SDGs (entity page rows)
        // ============================================================
        {
            key: "description",
            entityToFilter: "sdgs",
            type: "search",
            category: "other",
            icon: "mdi-sprout-outline",
            extractFn: (e) => e.description,
        },

        // ============================================================
        // SIMPLE DESCRIPTION CONFIGS (source-types, institution-types, licenses, oa-statuses)
        // ============================================================
        {
            key: "description",
            entityToFilter: "source-types",
            type: "search",
            category: "other",
            icon: "mdi-shape-outline",
            extractFn: (e) => e.description,
        },
        {
            key: "description",
            entityToFilter: "institution-types",
            type: "search",
            category: "other",
            icon: "mdi-shape-outline",
            extractFn: (e) => e.description,
        },
        {
            key: "description",
            entityToFilter: "licenses",
            type: "search",
            category: "other",
            icon: "mdi-lock-open-outline",
            extractFn: (e) => e.description,
        },
        {
            key: "description",
            entityToFilter: "oa-statuses",
            type: "search",
            category: "other",
            icon: "mdi-lock-open-outline",
            extractFn: (e) => e.description,
        },
        {
            key: "description",
            entityToFilter: "indexes",
            type: "search",
            category: "other",
            icon: "mdi-tag-outline",
            extractFn: (e) => e.description,
        },
        {
            key: "description",
            entityToFilter: "study-designs",
            type: "search",
            category: "other",
            icon: "mdi-flask-outline",
            extractFn: (e) => e.description,
        },
        {
            // oxjob #1312: the PubMed publication types mapped into this design.
            // Entity-page row only; not a filter.
            key: "pubmed_publication_types",
            entityToFilter: "study-designs",
            displayName: "PubMed publication types",
            type: "selectEntity",
            actions: [],
            category: "other",
            icon: "mdi-flask-outline",
            extractFn: (e) => e.pubmed_publication_types,
        },
        {
            key: "description",
            entityToFilter: "source-lists",
            type: "search",
            category: "other",
            icon: "mdi-format-list-bulleted",
            extractFn: (e) => e.description,
        },
        {
            key: "maintainer",
            entityToFilter: "source-lists",
            type: "search",
            category: "other",
            icon: "mdi-format-list-bulleted",
            extractFn: (e) => e.maintainer,
        },
        {
            key: "url",
            entityToFilter: "source-lists",
            displayName: "URL",
            type: "selectEntity",
            actions: [],
            category: "other",
            icon: "mdi-web",
            extractFn: (e) => e.url,
        },
        {
            key: "list_version",
            entityToFilter: "source-lists",
            displayName: "edition",
            type: "search",
            actions: ["column"],
            category: "other",
            icon: "mdi-format-list-bulleted",
            extractFn: (e) => e.list_version,
            column: { render: { kind: "date" } },
        },
        {
            key: "sources_count",
            entityToFilter: "source-lists",
            displayName: "sources",
            type: "range",
            actions: ["filter", "sort", "column"],
            category: "citation",
            icon: "mdi-book-open-outline",
            isDisplayedAsCount: true,
            extractFn: (e) => e.sources_count,
        },

        // ============================================================
        // DISPLAY NAME CONFIGS FOR SECONDARY ENTITY TYPES
        // (enables column view in SERP table)
        // ============================================================
        {
            key: "display_name",
            isIdentityColumn: true,
            entityToFilter: "topics",
            type: "search",
            actions: ["column"],
            category: "other",
            icon: "mdi-tag-outline",
            extractFn: (entity) => entity.display_name,
        },
        {
            key: "display_name",
            isIdentityColumn: true,
            entityToFilter: "subfields",
            type: "search",
            actions: ["column"],
            category: "other",
            icon: "mdi-tag-outline",
            extractFn: (entity) => entity.display_name,
        },
        {
            key: "display_name",
            isIdentityColumn: true,
            entityToFilter: "fields",
            type: "search",
            actions: ["column"],
            category: "other",
            icon: "mdi-tag-outline",
            extractFn: (entity) => entity.display_name,
        },
        {
            key: "display_name",
            isIdentityColumn: true,
            entityToFilter: "domains",
            type: "search",
            actions: ["column"],
            category: "other",
            icon: "mdi-tag-outline",
            extractFn: (entity) => entity.display_name,
        },
        {
            key: "display_name",
            isIdentityColumn: true,
            entityToFilter: "publishers",
            type: "search",
            actions: ["sort", "column"],
            actionsPopular: ["sort", "column"],
            category: "other",
            icon: "mdi-domain",
            extractFn: (entity) => entity.display_name,
        },
        {
            key: "display_name",
            isIdentityColumn: true,
            entityToFilter: "types",
            type: "search",
            actions: ["column"],
            category: "other",
            icon: "mdi-shape-outline",
            extractFn: (entity) => entity.display_name,
        },
        {
            key: "display_name",
            isIdentityColumn: true,
            entityToFilter: "continents",
            type: "search",
            actions: ["column"],
            category: "other",
            icon: "mdi-earth",
            extractFn: (entity) => entity.display_name,
        },
        {
            key: "display_name",
            isIdentityColumn: true,
            entityToFilter: "countries",
            type: "search",
            actions: ["column"],
            category: "other",
            icon: "mdi-earth",
            extractFn: (entity) => entity.display_name,
        },
        {
            key: "display_name",
            isIdentityColumn: true,
            entityToFilter: "languages",
            type: "search",
            actions: ["column"],
            category: "other",
            icon: "mdi-translate",
            extractFn: (entity) => entity.display_name,
        },
        {
            key: "display_name",
            isIdentityColumn: true,
            entityToFilter: "sdgs",
            type: "search",
            actions: ["column"],
            category: "other",
            icon: "mdi-sprout-outline",
            extractFn: (entity) => entity.display_name,
        },
        {
            key: "display_name",
            isIdentityColumn: true,
            entityToFilter: "source-types",
            type: "search",
            actions: ["column"],
            category: "other",
            icon: "mdi-shape-outline",
            extractFn: (entity) => entity.display_name,
        },
        {
            key: "display_name",
            isIdentityColumn: true,
            entityToFilter: "institution-types",
            type: "search",
            actions: ["column"],
            category: "other",
            icon: "mdi-shape-outline",
            extractFn: (entity) => entity.display_name,
        },
        {
            key: "display_name",
            isIdentityColumn: true,
            entityToFilter: "licenses",
            type: "search",
            actions: ["column"],
            category: "other",
            icon: "mdi-lock-open-outline",
            extractFn: (entity) => entity.display_name,
        },
        {
            key: "display_name",
            isIdentityColumn: true,
            entityToFilter: "oa-statuses",
            type: "search",
            actions: ["column"],
            category: "other",
            icon: "mdi-lock-open-outline",
            extractFn: (entity) => entity.display_name,
        },
        {
            key: "display_name",
            isIdentityColumn: true,
            entityToFilter: "indexes",
            type: "search",
            actions: ["column"],
            category: "other",
            icon: "mdi-tag-outline",
            extractFn: (entity) => entity.display_name,
        },
        {
            key: "display_name",
            isIdentityColumn: true,
            entityToFilter: "study-designs",
            type: "search",
            actions: ["column"],
            category: "other",
            icon: "mdi-flask-outline",
            extractFn: (entity) => entity.display_name,
        },
        {
            key: "display_name",
            isIdentityColumn: true,
            entityToFilter: "source-lists",
            type: "search",
            actions: ["column"],
            category: "other",
            icon: "mdi-format-list-bulleted",
            extractFn: (entity) => entity.display_name,
        },
        {
            key: "display_name",
            isIdentityColumn: true,
            entityToFilter: "keywords",
            type: "search",
            actions: ["column"],
            category: "other",
            icon: "mdi-tag-outline",
            extractFn: (entity) => entity.display_name,
        },
        {
            key: "description",
            entityToFilter: "keywords",
            type: "selectEntity",
            category: "other",
            actions: [],
            actionsPopular: [],
            icon: "mdi-text",
            extractFn: (entity) => entity.description,
        },
        {
            key: "ids.wikidata",
            entityToFilter: "keywords",
            displayName: "Wikidata ID",
            isId: true,
            type: "selectEntity",
            category: "ids",
            actions: [],
            actionsPopular: [],
            icon: "mdi-web",
            extractFn: (e) => e.ids?.wikidata,
        },
        // oxjob #1307: a keyword's related topics, weighted and many-to-many (never "parent"); score = share of the
        // keyword's works in the topic, shown as a % on the entity page.
        {
            key: "primary_topic.id",
            entityToFilter: "keywords",
            displayName: "primary topic",
            showScorePercent: true,
            entityToSelect: "topics",
            type: "selectEntity",
            isManyOptions: true,
            category: "other",
            actions: ["filter", "group_by"],
            actionsPopular: ["filter", "group_by"],
            icon: "mdi-tag-outline",
            extractFn: (entity) => entity.primary_topic,
        },
        {
            key: "topics.id",
            entityToFilter: "keywords",
            displayName: "related topics",
            showScorePercent: true,
            entityToSelect: "topics",
            type: "selectEntity",
            isManyOptions: true,
            category: "other",
            actions: ["filter"],
            actionsPopular: [],
            icon: "mdi-tag-outline",
            extractFn: (entity) => entity.topics,
        },
        // ------------------------------------------------------------------
        // Strict GUI==OQL parity additions (oxjob #573, generated): every
        // curated OQL word is filter-faceted on every entity where it parses
        // (exclusions: date axes/bounds, self-ids, search scopes, locations —
        // see elastic-api scripts/check_curated_core_parity.py). Filter-only
        // facets: no extractFn/column config needed.
        // ------------------------------------------------------------------
        {
            key: "citation_normalized_percentile.value",
            entityToFilter: "works",
            type: "range",
            category: "other",
            actions: ["filter",],
            actionsPopular: [],
            icon: "mdi-dots-horizontal",
        },
        {
            key: "citation_normalized_percentile.is_in_top_1_percent",
            entityToFilter: "works",
            type: "boolean",
            booleanValues: ["not top 1% cited", "top 1% cited"],
            category: "other",
            actions: ["filter",],
            actionsPopular: [],
            icon: "mdi-dots-horizontal",
        },
        {
            key: "citation_normalized_percentile.is_in_top_10_percent",
            entityToFilter: "works",
            type: "boolean",
            booleanValues: ["not top 10% cited", "top 10% cited"],
            category: "other",
            actions: ["filter",],
            actionsPopular: [],
            icon: "mdi-dots-horizontal",
        },
        {
            key: "topics.id",
            entityToFilter: "works",
            entityToSelect: "topics",
            type: "selectEntity",
            isManyOptions: true,
            category: "other",
            actions: ["filter",],
            actionsPopular: [],
            icon: "mdi-dots-horizontal",
        },
        // oxjob #1554 (Jason, 2026-10-10): no "biblio.volume" facet. "volume" is the volume
        // itself (volume.id above, a chapter's book); filtering on the citation's volume
        // number is no longer supported (the API keeps the value in the work record only).
        {
            key: "biblio.issue",
            entityToFilter: "works",
            type: "search",
            category: "other",
            actions: ["filter",],
            actionsPopular: [],
            icon: "mdi-dots-horizontal",
        },
        {
            key: "biblio.first_page",
            entityToFilter: "works",
            type: "search",
            category: "other",
            actions: ["filter",],
            actionsPopular: [],
            icon: "mdi-dots-horizontal",
        },
        {
            key: "biblio.last_page",
            entityToFilter: "works",
            type: "search",
            category: "other",
            actions: ["filter",],
            actionsPopular: [],
            icon: "mdi-dots-horizontal",
        },
        {
            key: "ids.mag",
            entityToFilter: "works",
            type: "search",
            category: "ids",
            actions: ["filter",],
            actionsPopular: [],
            icon: "mdi-tag-outline",
        },
        {
            key: "ids.pmid",
            entityToFilter: "works",
            type: "search",
            category: "ids",
            actions: ["filter", "column",],
            // PMID column (ZD 24281): opt this search-type id in as a column;
            // renders + exports the same full URL the API returns (like DOI).
            column: { render: { kind: "stringList" } },
            extractFn: (entity) => entity.ids?.pmid,
            actionsPopular: [],
            icon: "mdi-tag-outline",
        },
        {
            key: "ids.pmcid",
            entityToFilter: "works",
            type: "search",
            category: "ids",
            actions: ["filter",],
            actionsPopular: [],
            icon: "mdi-tag-outline",
        },
        {
            key: "referenced_works",
            entityToFilter: "works",
            entityToSelect: "works",
            type: "selectEntity",
            isManyOptions: true,
            category: "other",
            actions: ["filter",],
            actionsPopular: [],
            icon: "mdi-dots-horizontal",
        },
        {
            key: "locations_count",
            entityToFilter: "works",
            type: "range",
            category: "other",
            actions: ["filter",],
            actionsPopular: [],
            icon: "mdi-dots-horizontal",
        },
        {
            key: "has_references",
            entityToFilter: "works",
            extractFn: derivedWorkBoolean["has_references"],
            type: "boolean",
            booleanValues: ["not has references", "has references"],
            category: "other",
            actions: ["filter",],
            actionsPopular: [],
            icon: "mdi-dots-horizontal",
        },
        {
            key: "has_pmcid",
            entityToFilter: "works",
            extractFn: derivedWorkBoolean["has_pmcid"],
            type: "boolean",
            booleanValues: ["not has PMCID", "has PMCID"],
            category: "ids",
            actions: ["filter",],
            actionsPopular: [],
            icon: "mdi-tag-outline",
        },
        {
            key: "is_paratext",
            entityToFilter: "works",
            type: "boolean",
            booleanValues: ["not is paratext", "is paratext"],
            category: "other",
            actions: ["filter",],
            actionsPopular: [],
            icon: "mdi-dots-horizontal",
        },
        {
            key: "best_open_version",
            entityToFilter: "works",
            type: "search",
            category: "other",
            actions: ["filter",],
            actionsPopular: [],
            icon: "mdi-dots-horizontal",
        },
        {
            key: "has_fulltext",
            entityToFilter: "works",
            type: "boolean",
            booleanValues: ["not has fulltext", "has fulltext"],
            category: "other",
            actions: ["filter",],
            actionsPopular: [],
            icon: "mdi-dots-horizontal",
        },
        {
            key: "primary_location.is_oa",
            entityToFilter: "works",
            type: "boolean",
            booleanValues: ["not primary OA", "primary OA"],
            category: "other",
            actions: ["filter",],
            actionsPopular: [],
            icon: "mdi-dots-horizontal",
        },
        {
            key: "primary_location.is_published",
            entityToFilter: "works",
            type: "boolean",
            booleanValues: ["not primary published", "primary published"],
            category: "other",
            actions: ["filter",],
            actionsPopular: [],
            icon: "mdi-dots-horizontal",
        },
        {
            key: "primary_location.is_accepted",
            entityToFilter: "works",
            type: "boolean",
            booleanValues: ["not primary accepted", "primary accepted"],
            category: "other",
            actions: ["filter",],
            actionsPopular: [],
            icon: "mdi-dots-horizontal",
        },
        {
            key: "primary_location.source.has_issn",
            entityToFilter: "works",
            extractFn: derivedWorkBoolean["primary_location.source.has_issn"],
            type: "boolean",
            booleanValues: ["not has ISSN", "has ISSN"],
            category: "other",
            actions: ["filter",],
            actionsPopular: [],
            icon: "mdi-dots-horizontal",
        },
        {
            key: "best_oa_location.source.is_in_doaj",
            entityToFilter: "works",
            type: "boolean",
            booleanValues: ["not best OA source DOAJ", "best OA source DOAJ"],
            category: "other",
            actions: ["filter",],
            actionsPopular: [],
            icon: "mdi-dots-horizontal",
        },
        {
            key: "best_oa_location.source.listed_in",
            entityToFilter: "works",
            entityToSelect: "source-lists",
            displayName: "best OA source listed in",
            type: "selectEntity",
            category: "other",
            actions: ["filter", "group_by",],
            actionsPopular: [],
            icon: "mdi-dots-horizontal",
        },
        {
            key: "is_oa",
            entityToFilter: "works",
            extractFn: derivedWorkBoolean["is_oa"],
            displayName: "is oa",
            type: "boolean",
            booleanValues: ["not is oa", "is oa"],
            category: "other",
            actions: ["filter",],
            actionsPopular: [],
            icon: "mdi-dots-horizontal",
        },
        {
            key: "locations.is_oa",
            entityToFilter: "works",
            type: "boolean",
            booleanValues: ["not any location OA", "any location OA"],
            category: "other",
            actions: ["filter",],
            actionsPopular: [],
            icon: "mdi-dots-horizontal",
        },
        {
            key: "locations.is_published",
            entityToFilter: "works",
            type: "boolean",
            booleanValues: ["not any location published", "any location published"],
            category: "other",
            actions: ["filter",],
            actionsPopular: [],
            icon: "mdi-dots-horizontal",
        },
        {
            key: "locations.is_accepted",
            entityToFilter: "works",
            type: "boolean",
            booleanValues: ["not any location accepted", "any location accepted"],
            category: "other",
            actions: ["filter",],
            actionsPopular: [],
            icon: "mdi-dots-horizontal",
        },
        {
            key: "locations.source.is_core",
            entityToFilter: "works",
            type: "boolean",
            booleanValues: ["not any location CWTS core", "any location CWTS core"],
            category: "other",
            actions: ["filter",],
            actionsPopular: [],
            icon: "mdi-dots-horizontal",
        },
        {
            key: "locations.source.is_in_doaj",
            entityToFilter: "works",
            type: "boolean",
            booleanValues: ["not any location DOAJ", "any location DOAJ"],
            category: "other",
            actions: ["filter",],
            actionsPopular: [],
            icon: "mdi-dots-horizontal",
        },
        {
            key: "locations.source.listed_in",
            entityToFilter: "works",
            entityToSelect: "source-lists",
            displayName: "any location source listed in",
            type: "selectEntity",
            category: "other",
            actions: ["filter", "group_by",],
            actionsPopular: [],
            icon: "mdi-dots-horizontal",
        },
        {
            key: "has_oa_submitted_version",
            entityToFilter: "works",
            extractFn: derivedWorkBoolean["has_oa_submitted_version"],
            type: "boolean",
            booleanValues: ["not has oa submitted version", "has oa submitted version"],
            category: "other",
            actions: ["filter",],
            actionsPopular: [],
            icon: "mdi-dots-horizontal",
        },
        {
            key: "primary_location.license",
            entityToFilter: "works",
            entityToSelect: "licenses",
            type: "selectEntity",
            category: "other",
            actions: ["filter",],
            actionsPopular: [],
            icon: "mdi-dots-horizontal",
        },
        {
            key: "best_oa_location.source.issn",
            entityToFilter: "works",
            type: "search",
            category: "ids",
            actions: ["filter",],
            actionsPopular: [],
            icon: "mdi-tag-outline",
        },
        {
            key: "locations.source.issn",
            entityToFilter: "works",
            type: "search",
            category: "ids",
            actions: ["filter",],
            actionsPopular: [],
            icon: "mdi-tag-outline",
        },
        {
            key: "primary_location.version",
            entityToFilter: "works",
            type: "search",
            category: "other",
            actions: ["filter",],
            actionsPopular: [],
            icon: "mdi-dots-horizontal",
        },
        {
            key: "locations.version",
            entityToFilter: "works",
            type: "search",
            category: "other",
            actions: ["filter",],
            actionsPopular: [],
            icon: "mdi-dots-horizontal",
        },
        {
            key: "best_oa_location.source.type",
            entityToFilter: "works",
            entityToSelect: "source-types",
            type: "selectEntity",
            category: "other",
            actions: ["filter",],
            actionsPopular: [],
            icon: "mdi-dots-horizontal",
        },
        {
            key: "locations.source.type",
            entityToFilter: "works",
            entityToSelect: "source-types",
            type: "selectEntity",
            category: "other",
            actions: ["filter",],
            actionsPopular: [],
            icon: "mdi-dots-horizontal",
        },
        {
            key: "best_oa_location.source.id",
            entityToFilter: "works",
            entityToSelect: "sources",
            type: "selectEntity",
            isManyOptions: true,
            category: "other",
            actions: ["filter",],
            actionsPopular: [],
            icon: "mdi-dots-horizontal",
        },
        // oxjob #1526: current institution's region and city, looked up in the institutions index.
        {
            key: "last_known_institutions.geo.region",
            entityToFilter: "authors",
            displayName: "institution region",
            type: "search",
            quoteValue: true,
            category: "institution",
            actions: ["filter"],
            actionsPopular: [],
            icon: "mdi-map-marker-outline",
        },
        {
            key: "last_known_institutions.geo.city",
            entityToFilter: "authors",
            displayName: "institution city",
            type: "search",
            quoteValue: true,
            category: "institution",
            actions: ["filter"],
            actionsPopular: [],
            icon: "mdi-map-marker-outline",
        },
        {
            key: "topics.id",
            entityToFilter: "authors",
            entityToSelect: "topics",
            type: "selectEntity",
            isManyOptions: true,
            category: "other",
            actions: ["filter",],
            actionsPopular: [],
            icon: "mdi-dots-horizontal",
        },
        // oxjob #1526: authors by the subfield, field and domain of their topics.
        {
            key: "topics.subfield.id",
            entityToFilter: "authors",
            entityToSelect: "subfields",
            displayName: "Subfield",
            type: "selectEntity",
            isManyOptions: true,
            category: "other",
            actions: ["filter", "group_by"],
            actionsPopular: [],
            icon: "mdi-tag-outline",
        },
        {
            key: "topics.field.id",
            entityToFilter: "authors",
            entityToSelect: "fields",
            displayName: "Field",
            type: "selectEntity",
            isManyOptions: true,
            category: "other",
            actions: ["filter", "group_by"],
            actionsPopular: [],
            icon: "mdi-tag-outline",
        },
        {
            key: "topics.domain.id",
            entityToFilter: "authors",
            entityToSelect: "domains",
            displayName: "Domain",
            type: "selectEntity",
            isManyOptions: true,
            category: "other",
            actions: ["filter", "group_by"],
            actionsPopular: [],
            icon: "mdi-tag-outline",
        },
        {
            key: "orcid",
            entityToFilter: "authors",
            type: "search",
            category: "ids",
            actions: ["filter",],
            actionsPopular: [],
            icon: "mdi-tag-outline",
        },
        {
            key: "topics.id",
            entityToFilter: "institutions",
            entityToSelect: "topics",
            type: "selectEntity",
            isManyOptions: true,
            category: "other",
            actions: ["filter",],
            actionsPopular: [],
            icon: "mdi-dots-horizontal",
        },
        // oxjob #1526: institutions by the subfield, field and domain of their topics.
        {
            key: "topics.subfield.id",
            entityToFilter: "institutions",
            entityToSelect: "subfields",
            displayName: "Subfield",
            type: "selectEntity",
            isManyOptions: true,
            category: "other",
            actions: ["filter", "group_by"],
            actionsPopular: [],
            icon: "mdi-tag-outline",
        },
        {
            key: "topics.field.id",
            entityToFilter: "institutions",
            entityToSelect: "fields",
            displayName: "Field",
            type: "selectEntity",
            isManyOptions: true,
            category: "other",
            actions: ["filter", "group_by"],
            actionsPopular: [],
            icon: "mdi-tag-outline",
        },
        {
            key: "topics.domain.id",
            entityToFilter: "institutions",
            entityToSelect: "domains",
            displayName: "Domain",
            type: "selectEntity",
            isManyOptions: true,
            category: "other",
            actions: ["filter", "group_by"],
            actionsPopular: [],
            icon: "mdi-tag-outline",
        },
        {
            key: "continent",
            entityToFilter: "institutions",
            entityToSelect: "continents",
            type: "selectEntity",
            category: "geo",
            actions: ["filter",],
            actionsPopular: [],
            icon: "mdi-map-marker-outline",
        },
        {
            key: "host_organization",
            entityToFilter: "sources",
            entityToSelect: "publishers",
            type: "selectEntity",
            isManyOptions: true,
            category: "other",
            actions: ["filter",],
            actionsPopular: [],
            icon: "mdi-dots-horizontal",
        },
        {
            key: "ids.mag",
            entityToFilter: "sources",
            type: "search",
            category: "ids",
            actions: ["filter",],
            actionsPopular: [],
            icon: "mdi-tag-outline",
        },
        {
            key: "has_issn",
            entityToFilter: "sources",
            type: "boolean",
            booleanValues: ["not has issn", "has issn"],
            category: "ids",
            actions: ["filter",],
            actionsPopular: [],
            icon: "mdi-tag-outline",
        },
        {
            key: "continent",
            entityToFilter: "sources",
            entityToSelect: "continents",
            type: "selectEntity",
            category: "geo",
            actions: ["filter",],
            actionsPopular: [],
            icon: "mdi-map-marker-outline",
        },
        {
            key: "continent",
            entityToFilter: "publishers",
            // Normalized to selectEntity + entityToSelect:"continents" to match
            // institutions/sources (was type:"search"); consistent `type` per the
            // oxjob #621 D3 cleanup. `geo` is added to facetCategories.publishers
            // so this no longer drops out of the picker.
            entityToSelect: "continents",
            type: "selectEntity",
            category: "geo",
            actions: ["filter",],
            actionsPopular: [],
            icon: "mdi-map-marker-outline",
        },
        {
            key: "continent",
            entityToFilter: "funders",
            entityToSelect: "continents",
            type: "selectEntity",
            category: "geo",
            actions: ["filter",],
            actionsPopular: [],
            icon: "mdi-map-marker-outline",
        },
        {
            key: "summary_stats.h_index",
            entityToFilter: "concepts",
            type: "range",
            category: "citation",
            actions: ["filter",],
            actionsPopular: [],
            icon: "mdi-format-quote-close",
        },
        {
            key: "summary_stats.i10_index",
            entityToFilter: "concepts",
            type: "range",
            category: "citation",
            actions: ["filter",],
            actionsPopular: [],
            icon: "mdi-format-quote-close",
        },
        {
            key: "summary_stats.2yr_mean_citedness",
            entityToFilter: "concepts",
            type: "range",
            category: "citation",
            actions: ["filter",],
            actionsPopular: [],
            icon: "mdi-format-quote-close",
        },
        {
            key: "fields.id",
            entityToFilter: "domains",
            entityToSelect: "fields",
            type: "selectEntity",
            category: "other",
            actions: ["filter",],
            actionsPopular: [],
            icon: "mdi-dots-horizontal",
        },
        {
            key: "subfields.id",
            entityToFilter: "fields",
            entityToSelect: "subfields",
            type: "selectEntity",
            category: "other",
            actions: ["filter",],
            actionsPopular: [],
            icon: "mdi-dots-horizontal",
        },
        {
            key: "topics.id",
            entityToFilter: "subfields",
            // Normalized to selectEntity + entityToSelect:"topics" to match
            // works/authors/sources/institutions (was type:"search"); consistent
            // `type` per the oxjob #621 D3 cleanup.
            entityToSelect: "topics",
            type: "selectEntity",
            category: "other",
            actions: ["filter",],
            actionsPopular: [],
            icon: "mdi-dots-horizontal",
        },
        {
            key: "topics.id",
            entityToFilter: "awards",
            entityToSelect: "topics",
            type: "selectEntity",
            category: "other",
            actions: ["filter",],
            actionsPopular: [],
            icon: "mdi-dots-horizontal",
        },
    ]

    // #573 strict GUI==OQL parity: the former noFilterEntities withholding
    // (counts sort/column-only on the vocab entities) is gone — works_count and
    // cited_by_count are filterable on every entity that carries them.
    const worksCountFilters = getEntityConfigs()
        .map(c => c.name)
        // #294: exclude 'locations' too — /locations has no works_count/cited_by_count
        // field, so the injected filter+sort chips 400.
        .filter(name => name !== 'works' && name !== 'awards' && name !== 'locations')
        .map(name => {
            return {
                key: "works_count",
                entityToFilter: name,
                type: "range",
                category: "citation",
                actions: ["filter", "sort", "column"],
                actionsPopular: ["sort", "column",],
                icon: "mdi-file-document-multiple-outline",
                isDisplayedAsCount: true,
                extractFn: (entity) => entity.works_count,
            }
        })

    const citedByCountFilters = getEntityConfigs()
        .map(c => c.name)
        // #294: exclude 'locations' too — /locations has no works_count/cited_by_count
        // field, so the injected filter+sort chips 400.
        .filter(name => name !== 'works' && name !== 'awards' && name !== 'locations')
        .map(name => {
            return {
                key: "cited_by_count",
                entityToFilter: name,
                type: "range",
                category: "citation",
                actions: ["filter", "column", "sort"],
                actionsPopular: ["column", "sort"],
                icon: "mdi-format-quote-close",
                isDisplayedAsCount: true,
                extractFn: (entity) => entity.cited_by_count,
            }
        })

    // Institutions also count works through their child institutions, then also through
    // the institutions they replaced (= the lineage filter): every count is shown on the
    // entity page, so a librarian sees which number is which (oxjob #1823).
    const institutionFamilyCountFilters = [
        "works_count_with_children",
        "works_count_with_children_and_predecessors",
    ].map(key => ({
        key,
        entityToFilter: "institutions",
        type: "range",
        category: "citation",
        actions: ["filter", "sort", "column"],
        actionsPopular: [],
        icon: "mdi-file-document-multiple-outline",
        isDisplayedAsCount: true,
        extractFn: (entity) => entity[key],
    }))

    // Inject a `collection` facet for every entity type that supports collections (v1).
    // The chip itself resolves values via /collections/<id> (see NoviceFilterChip)
    // and renders a picker that lists the current user's collections for the type.
    // facetConfigUtils.getFacetConfig also short-circuits on key === "collection"
    // for the same shape — both surfaces use this config.
    const COLLECTION_FACET_ENTITY_TYPES = [
        "works", "authors", "sources", "institutions",
        "topics", "sdgs", "funders", "publishers", "keywords", "concepts",
    ]
    const collectionFilters = COLLECTION_FACET_ENTITY_TYPES.map(name => ({
        key: "collection",
        entityToFilter: name,
        entityToSelect: "collections",
        // Human label only — the API filter key stays `collection:` (see #266/#228).
        // Per-entity "<Entity> is in collection" (oxjob #367): every collection filter
        // reads as membership of its type, and the standalone filter stops owning the
        // bare word "collection" (the #350 Zotero-overload trap). Sentence-cased, so it
        // is flagged `displayNameVerbatim` to bypass titleCase()/text-capitalize at the
        // chip + add-filter surfaces (which would wrongly yield "Work Is In Collection").
        displayName: collectionFilterLabel(name),
        displayNameVerbatim: true,
        type: "selectEntity",
        category: "other",
        actions: ["filter"],
        actionsPopular: [],
        icon: "mdi-folder-outline",
    }))

    const allConfigs = [
        ...ret,
        ...worksCountFilters,
        ...citedByCountFilters,
        ...institutionFamilyCountFilters,
        ...collectionFilters,
    ]

    const manipulated = allConfigs
        // .filter(f => onlyReturnTheseFacets.includes(f.key))
        .map(config => {
            return {
                ...config,
                // Facet labels derive from the server /meta catalog (oxjob
                // #424); an authored displayName is a client override and wins
                // (used where the server label is missing, wrongly cased, or
                // deliberately different — see displayNameVerbatim entries).
                // The bare-key fallback keeps the sort below crash-safe if a
                // facet is unknown to both.
                displayName: config.displayName
                    ?? getPropertyDisplayName(config.entityToFilter, config.key)
                    ?? config.key,
            }
        })
        .filter(config => {
            return !entityType || config.entityToFilter === entityType
        })

    manipulated.sort((a, b) => {
        return a.displayName.toLowerCase() > b.displayName.toLowerCase() ? 1 : -1
    })

    return manipulated
}


// Helper functions have been extracted to facetConfigUtils.js
// Import them from there: getFacetConfig, findFacetConfigs, facetsByCategory

export {
    facetConfigs,
    facetCategories,
    facetCategoriesIcons,
}
